import React, { useState, useEffect, useCallback, useRef } from "react"
import { Helmet } from "react-helmet"
import { useDispatch, useSelector } from "react-redux"
import { push, replace } from "connected-react-router"
import {
	delayedDispatch,
	setBreadcrumb,
	setLoader,
	setTitle,
	updateCrumb,
} from "store/actions"
import {
	ContentWrapper,
	GoogleMaps,
	Icon,
	Map,
	Table,
	ZoneModal,
	ZoneDeleteModal,
} from "components"
import { ZonePayload } from "components/ZoneModal"
import {
	geojsonToPolygonPaths,
	ZONE_MODES,
	ZONE_MODE_COLORS,
	ZoneMode,
	ContractZoneRow,
	contractZonesToRows,
	rowsToDeliveryZones,
	rowsToDeliveryModeZones,
} from "utils/geojson"
import { watchGroups, Group } from "firebase/groupsService"
import {
	Button,
	Col,
	DatePicker,
	Drawer,
	Input,
	notification,
	Row,
	Select,
	Popover,
	Switch,
	Tooltip,
} from "antd"
import { API, Endpoints } from "utils/api"
import Dropzone from "react-dropzone"
import Geocode from "react-geocode"
import moment from "moment"
import { LANGUAGES, translate } from "utils/utils"
import Strings from "utils/strings"
import { useParams } from "react-router-dom"

import "./styles.scss"
import excelImage from "assets/images/excel.png"
import pdfImage from "assets/images/pdf.png"
import {
	QuestionCircleOutlined,
	SettingOutlined,
	EyeOutlined,
	EyeInvisibleOutlined,
} from "@ant-design/icons"
import lodash from "lodash"
import { parsePhoneNumberFromString } from "libphonenumber-js"

type PreProcessingParamDef =
	| {
			key: string
			type: "float" | "int"
			default: number
			labelKey: keyof typeof Strings.contracts
	  }
	| {
			key: string
			type: "select"
			default: string
			labelKey: keyof typeof Strings.contracts
			options: { value: string; labelKey: keyof typeof Strings.contracts }[]
			helperKey: keyof typeof Strings.contracts
	  }

const PRE_PROCESSING_PARAM_DEFS: PreProcessingParamDef[] = [
	{
		key: "nucleoid_distance",
		type: "float",
		default: 0,
		labelKey: "preProcessingNucleoidDistance",
	},
	{
		key: "max_van_stops_per_hour",
		type: "int",
		default: 0,
		labelKey: "preProcessingMaxVanStopsPerHour",
	},
	{
		key: "vicinity_distance",
		type: "float",
		default: 0.0,
		labelKey: "preProcessingVicinityDistance",
	},
	{
		key: "street_priority",
		type: "select",
		default: "rank",
		labelKey: "preProcessingStreetPriority",
		options: [
			{ value: "none", labelKey: "preProcessingStreetPriorityNone" },
			{ value: "rank", labelKey: "preProcessingStreetPriorityRank" },
			{ value: "score", labelKey: "preProcessingStreetPriorityScore" },
		],
		helperKey: "preProcessingStreetPriorityHelper",
	},
]

const ContractDetail = () => {
	const dispatch = useDispatch()
	const { id, contractId } = useParams<{ id: string; contractId: string }>()
	const mobile = useSelector((state: any) => state.mobile)

	const searchTimerRef = useRef<any>(null)
	const smsTemplateRef = useRef<any>(null)

	// Contract data
	const [contract, setContract] = useState<any>(null)
	const [business, setBusiness] = useState<any>(null)
	const [name, setName] = useState("")
	const [description, setDescription] = useState("")
	const [type, setType] = useState<any>(null)
	const [startDate, setStartDate] = useState<any>(null)
	const [endDate, setEndDate] = useState<any>(null)
	const [searchAddress, setSearchAddress] = useState("")
	const [basePoint, setBasePoint] = useState<any>({
		type: "Point",
		coordinates: [],
	})
	const [warehouseId, setWarehouseId] = useState("")
	const [loadSequenceType, setLoadSequenceType] = useState("")

	// Lists
	const [workAreas, setWorkAreas] = useState<any[]>([
		{ start: null, end: null },
	])
	const [lots, setLots] = useState<any[]>([])
	// The geojson library: zones owned by this contract plus every global one.
	const [availableZones, setAvailableZones] = useState<any[]>([])
	// One row per (zone, mode); split across deliveryZones / deliveryModeZones on save.
	const [zoneRows, setZoneRows] = useState<ContractZoneRow[]>([])
	const [showZoneModal, setShowZoneModal] = useState(false)
	// Geojson being created or edited (null while the modal is closed).
	const [zoneToEdit, setZoneToEdit] = useState<any | null>(null)
	// Hidden zone keys for the map view.
	const [hiddenZoneKeys, setHiddenZoneKeys] = useState<string[]>([])
	// Geojson pending deletion (drives the delete-confirmation modal).
	const [zoneToDelete, setZoneToDelete] = useState<any | null>(null)
	// Server-side refusal for the pending deletion, e.g. a global zone in use.
	const [zoneDeleteError, setZoneDeleteError] = useState<string | null>(null)
	const [attachments, setAttachments] = useState<any[]>([])
	const [drivers, setDrivers] = useState<any[]>([])
	const [vehicles, setVehicles] = useState<any[]>([])
	const [operators, setOperators] = useState<any[]>([])
	const [shifts, setShifts] = useState<any[]>([])
	const [defaultShift, setDefaultShift] = useState<any>(null)

	// Defaults (dropdown options)
	const [defaultDrivers, setDefaultDrivers] = useState<any[]>([])
	const [defaultVehicles, setDefaultVehicles] = useState<any[]>([])
	const [defaultOperators, setDefaultOperators] = useState<any[]>([])
	const [defaultShifts, setDefaultShifts] = useState<any[]>([])
	const [defaultContractTypes, setDefaultContractTypes] = useState<any[]>([])

	// Settings objects
	const [busParams, setBusParams] = useState<any>({})
	const [serviceTimes, setServiceTimes] = useState<any>({
		serviceTimeA: 300,
		serviceTimeB: 0,
	})
	const [preProcessingParameters, setPreProcessingParameters] = useState<any>(
		{}
	)
	const [preProcessingCollapsed, setPreProcessingCollapsed] = useState(true)
	const [functionalities, setFunctionalities] = useState<any>({})
	const [smsOptions, setSmsOptions] = useState<any>({})
	const [scannerEmailTemplate, setScannerEmailTemplate] = useState<any>({})
	const [externalDelivery, setExternalDelivery] = useState<any>({})
	const [containerSizes, setContainerSizes] = useState<any>({})
	const [globalSettings, setGlobalSettings] = useState<any>({})
	const [smsTestModePhoneList, setSmsTestModePhoneList] = useState<any>(null)

	// UI state
	const [hasUnsavedFields, setHasUnsavedFields] = useState(false)
	const [showUberWarehouseMap, setShowUberWarehouseMap] = useState(false)
	const [messagingGroups, setMessagingGroups] = useState<Group[]>([])
	const [isActive, setIsActive] = useState(true)
	const [language, setLanguage] = useState("pt")
	const [filesToDelete, setFilesToDelete] = useState<any[]>([])
	const [showAttachmentsDrawer, setShowAttachmentsDrawer] = useState(false)
	const [tempAttachment, setTempAttachment] = useState<any>(null)
	const [smsTestModePhoneListSearchValue, setSmsTestModePhoneListSearchValue] =
		useState("")
	const [
		scannerEmailRecipientsSearchValue,
		setScannerEmailRecipientsSearchValue,
	] = useState("")
	const [scannerEmailCCSearchValue, setScannerEmailCCSearchValue] = useState("")
	const [scannerEmailBCCSearchValue, setScannerEmailBCCSearchValue] =
		useState("")

	// Helper to apply contract data to all state
	const applyContractData = useCallback((contractData: any) => {
		setContract(contractData)
		setName(contractData.name || "")
		setDescription(contractData.description || "")
		setType(contractData.type)
		setStartDate(contractData.startDate || null)
		setEndDate(contractData.endDate || null)
		setSearchAddress(contractData.address || "")
		setBasePoint(contractData.basePoint || { type: "Point", coordinates: [] })
		setWarehouseId(contractData.warehouseId || "")
		setLoadSequenceType(contractData.loadSequenceType || "")
		setWorkAreas(contractData.workAreas || [{ start: null, end: null }])
		setZoneRows(contractZonesToRows(contractData))
		setLots(contractData.lots || [])
		setAttachments(contractData.attachments || [])
		setDrivers(contractData.drivers || [])
		setVehicles(contractData.vehicles || [])
		setOperators(contractData.operators || [])
		setShifts(contractData.shifts || [])
		setDefaultShift(contractData.defaultShift || null)
		setBusParams(contractData.busParams || {})
		setServiceTimes(
			contractData.serviceTimes || { serviceTimeA: 300, serviceTimeB: 0 }
		)
		setPreProcessingParameters(contractData.preProcessingParameters || {})
		setFunctionalities(contractData.functionalities || {})
		setSmsOptions(contractData.smsOptions || {})
		setScannerEmailTemplate(contractData.scannerEmailTemplate || {})
		setExternalDelivery(contractData.externalDelivery || {})
		setContainerSizes(contractData.containerSizes || {})
		setGlobalSettings(contractData.globalSettings || {})
		setSmsTestModePhoneList(contractData.smsTestModePhoneList || null)
		setIsActive(
			contractData.isActive !== undefined ? contractData.isActive : true
		)
	}, [])

	const getData = useCallback(async () => {
		dispatch(setLoader(true))

		try {
			if (contractId !== "new") {
				const [
					response,
					driversResponse,
					vehiclesResponse,
					staffResponse,
					shiftResponse,
					contractTypeResponse,
				] = await Promise.all([
					API.get({
						url: Endpoints.uriBusinesses(`${id}/contracts/${contractId}`),
					}),
					API.get({
						url: Endpoints.uriStaff("driver"),
					}),
					API.get({
						url: Endpoints.uriVehicles(),
					}),
					API.get({
						url: Endpoints.uriStaff("operator"),
					}),
					API.get({
						url: Endpoints.uriShifts(),
					}),
					API.get({
						url: Endpoints.uriContractTypes(),
					}),
				])

				if (
					response.ok &&
					driversResponse.ok &&
					vehiclesResponse.ok &&
					staffResponse.ok &&
					shiftResponse.ok &&
					contractTypeResponse.ok
				) {
					const { contract: contractData } = response.data.results || {}
					const { staff: driversList = [] } = driversResponse.data.results || {}
					const { vehicles: vehiclesList = [] } =
						vehiclesResponse.data.results || {}
					const { staff: operatorsList = [] } = staffResponse.data.results || {}
					const { shifts: shiftsList = [] } = shiftResponse.data.results || {}
					const { contractTypes: contractTypesList = [] } =
						contractTypeResponse.data.results || {}

					setDefaultDrivers(driversList)
					setDefaultVehicles(vehiclesList)
					setDefaultOperators(operatorsList)
					setDefaultShifts(shiftsList)
					setDefaultContractTypes(contractTypesList)
					applyContractData(contractData)
				} else {
					notification.error({
						message: Strings.serverErrors.title,
						description:
							response.data?.message || Strings.serverErrors.wentWrong,
						placement: "bottomRight",
						duration: 5,
					})
				}
			} else {
				const [
					response,
					driversResponse,
					vehiclesResponse,
					staffResponse,
					shiftResponse,
					contractTypeResponse,
				] = await Promise.all([
					API.get({
						url: Endpoints.uriBusinesses(id),
					}),
					API.get({
						url: Endpoints.uriStaff("driver"),
					}),
					API.get({
						url: Endpoints.uriVehicles(),
					}),
					API.get({
						url: Endpoints.uriStaff("operator"),
					}),
					API.get({
						url: Endpoints.uriShifts(),
					}),
					API.get({
						url: Endpoints.uriContractTypes(),
					}),
				])

				if (
					response.ok &&
					driversResponse.ok &&
					vehiclesResponse.ok &&
					staffResponse.ok &&
					shiftResponse.ok &&
					contractTypeResponse.ok
				) {
					const { business: businessData } = response.data.results || {}
					const { drivers: driversList = [] } =
						driversResponse.data.results || {}
					const { vehicles: vehiclesList = [] } =
						vehiclesResponse.data.results || {}
					const { staff: operatorsList = [] } = staffResponse.data.results || {}
					const { shifts: shiftsList = [] } = shiftResponse.data.results || {}
					const { contractTypes: contractTypesList = [] } =
						contractTypeResponse.data.results || {}

					setBusiness(businessData)
					setDefaultDrivers(driversList)
					setDefaultVehicles(vehiclesList)
					setDefaultOperators(operatorsList)
					setDefaultShifts(shiftsList)
					setDefaultContractTypes(contractTypesList)
				} else {
					dispatch(replace(`/businesses/${id}`))

					notification.error({
						message: Strings.businesses.contracts,
						description:
							response.data?.message || Strings.serverErrors.wentWrong,
						placement: "bottomRight",
						duration: 5,
					})
				}
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}

		dispatch(setLoader(false))
	}, [dispatch, id, contractId, applyContractData])

	const invalidInterval = useCallback(() => {
		notification.warn({
			message: Strings.businesses.contracts,
			description: Strings.contracts.invalidWorkAreaInterval,
			placement: "bottomRight",
			duration: 5,
		})
	}, [])

	const canAddWorkArea = useCallback(() => {
		const copyWorkAreas = JSON.parse(JSON.stringify(workAreas))

		let canAdd = workAreas.length === 0

		if (canAdd) {
			return true
		} else {
			const index = copyWorkAreas.length - 1
			const last = copyWorkAreas[index]

			if (last.start !== null && last.end !== null) {
				if (
					last.end < last.start ||
					last.start < 1000000 ||
					last.start > 9999999 ||
					last.end < 1000000 ||
					last.end > 9999999
				) {
					invalidInterval()
					return false
				}

				copyWorkAreas.splice(index, 1)

				for (const area of copyWorkAreas) {
					if (
						(last.start >= area.start && last.start <= area.end) ||
						(last.end >= area.start && last.end <= area.end) ||
						(last.start < area.start && last.end > area.end)
					) {
						invalidInterval()
						return false
					}
				}

				return true
			}
		}
	}, [workAreas, invalidInterval])

	const isValidExternalDelivery = useCallback(() => {
		if (!externalDelivery) return true

		for (const key of Object.keys(externalDelivery)) {
			const partner = externalDelivery[key]
			if (!partner?.active) continue

			if (
				!partner.wareHouseName?.trim() ||
				!partner.warehousePhone?.trim() ||
				!partner.storeId?.trim()
			) {
				notification.warn({
					message: Strings.contracts.externalDelivery,
					description: Strings.contracts.externalDeliveryFieldsMandatory,
					placement: "bottomRight",
					duration: 5,
				})
				return false
			}

			const phone = parsePhoneNumberFromString(partner.warehousePhone)
			if (!phone?.isValid()) {
				notification.warn({
					message: Strings.contracts.externalDelivery,
					description: Strings.contracts.externalDeliveryInvalidPhone,
					placement: "bottomRight",
					duration: 5,
				})
				return false
			}

			if (/\s/.test(partner.storeId)) {
				notification.warn({
					message: Strings.contracts.externalDelivery,
					description: Strings.contracts.externalDeliveryStoreIdNoSpaces,
					placement: "bottomRight",
					duration: 5,
				})
				return false
			}

			// An empty list would make every order ineligible; leaving the field
			// unset is how you say "accept all".
			if (partner.requirements?.acceptedPhoneTypes?.length === 0) {
				notification.warn({
					message: Strings.contracts.externalDelivery,
					description:
						Strings.contracts.externalDeliveryAcceptedPhoneTypesRequired,
					placement: "bottomRight",
					duration: 5,
				})
				return false
			}
		}

		return true
	}, [externalDelivery])

	const isValidContract = useCallback(() => {
		if (!startDate) {
			notification.warn({
				message: Strings.businesses.contracts,
				description: Strings.contracts.startDateMandatory,
				placement: "bottomRight",
				duration: 5,
			})
			return false
		}

		if (!endDate) {
			notification.warn({
				message: Strings.businesses.contracts,
				description: Strings.contracts.endDateMandatory,
				placement: "bottomRight",
				duration: 5,
			})
			return false
		}

		if (!type) {
			notification.warn({
				message: Strings.businesses.contracts,
				description: Strings.contracts.typeMandatory,
				placement: "bottomRight",
				duration: 5,
			})
			return false
		}

		if (!basePoint || basePoint?.coordinates.length <= 0) {
			notification.warn({
				message: Strings.businesses.contracts,
				description: Strings.contracts.baseLocationAddressMandatory,
				placement: "bottomRight",
				duration: 5,
			})
			return false
		}

		if (!canAddWorkArea()) {
			notification.warn({
				message: Strings.businesses.contracts,
				description: Strings.contracts.invalidWorkAreaInterval,
				placement: "bottomRight",
				duration: 5,
			})
			return false
		}

		if (!isValidExternalDelivery()) {
			return false
		}

		return true
	}, [
		startDate,
		endDate,
		type,
		basePoint,
		canAddWorkArea,
		isValidExternalDelivery,
	])

	const saveContract = useCallback(async () => {
		if (!isValidContract()) {
			return
		}

		const busParamsCopy = { ...busParams }
		Object.keys(busParamsCopy).forEach((key) => {
			if (key === "" || busParamsCopy[key] === "") {
				delete busParamsCopy[key]
				return
			} else {
				const value = busParamsCopy[key]
				const numValue = Number(value)

				if (
					typeof value === "string" &&
					value.trim() !== "" &&
					!Number.isNaN(numValue)
				) {
					busParamsCopy[key] = numValue
				}
			}
		})

		const preProcessingParametersCopy: Record<string, any> = {}
		PRE_PROCESSING_PARAM_DEFS.forEach((def) => {
			const raw = preProcessingParameters[def.key]
			if (raw === undefined || raw === null || raw === "") {
				return
			}
			if (def.type === "int" || def.type === "float") {
				const numValue = Number(raw)
				if (Number.isNaN(numValue)) {
					return
				}
				preProcessingParametersCopy[def.key] =
					def.type === "int" ? Math.trunc(numValue) : numValue
			} else {
				preProcessingParametersCopy[def.key] = raw
			}
		})

		// Validate container sizes
		const containerSizesCopy = containerSizes || {}
		if (containerSizesCopy.bagSizes) {
			const invalidBagSizes = containerSizesCopy.bagSizes.some((size: any) => {
				return (
					!size.size ||
					size.size <= 0 ||
					size.size > 1 ||
					!Number.isFinite(size.size) ||
					!size.name ||
					size.name.trim() === "" ||
					!size.display
				)
			})
			if (invalidBagSizes) {
				notification.warn({
					message: Strings.businesses.contracts,
					description: Strings.contracts.invalidBagSize,
					placement: "bottomRight",
					duration: 5,
				})
				return
			}
		}

		if (containerSizesCopy.outOfFormatSizes) {
			const invalidOutOfFormatSizes = containerSizesCopy.outOfFormatSizes.some(
				(size: any) => {
					return (
						!size.size ||
						size.size <= 0 ||
						!Number.isFinite(size.size) ||
						!size.name ||
						size.name.trim() === "" ||
						!size.display
					)
				}
			)
			if (invalidOutOfFormatSizes) {
				notification.warn({
					message: Strings.businesses.contracts,
					description: Strings.contracts.invalidOutOfFormatSize,
					placement: "bottomRight",
					duration: 5,
				})
				return
			}
		}

		const body = {
			name,
			type: type._id,
			startDate,
			endDate,
			description,
			address: searchAddress,
			basePoint,
			workAreas,
			deliveryZones: rowsToDeliveryZones(zoneRows),
			deliveryModeZones: rowsToDeliveryModeZones(zoneRows),
			lots,
			drivers,
			vehicles,
			operators,
			shifts,
			defaultShift: defaultShift || null,
			warehouseId,
			busParams: busParamsCopy,
			serviceTimes,
			preProcessingParameters: preProcessingParametersCopy,
			functionalities,
			smsOptions,
			scannerEmailTemplate,
			loadSequenceType,
			smsTestModePhoneList,
			containerSizes: containerSizesCopy,
			globalSettings,
			externalDelivery,
		}

		dispatch(setLoader(true))

		try {
			const isNew = contractId === "new"
			const request = isNew ? API.post : API.put

			const response = await request({
				url: Endpoints.uriBusinesses(
					isNew ? `${id}/contracts/` : `${id}/contracts/${contractId}`
				),
				data: body,
			})

			if (response.ok) {
				const { contract: contractData } = response.data.results || {}
				applyContractData(contractData)
				setHasUnsavedFields(false)

				if (isNew) {
					dispatch(push(`/businesses/${id}/contracts/${contractData._id}`))
					dispatch(setBreadcrumb(null))
				}

				notification.success({
					message: Strings.businesses.contracts,
					description: response.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			} else {
				notification.error({
					message: Strings.businesses.contracts,
					description: response.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}

		dispatch(setLoader(false))
	}, [
		isValidContract,
		name,
		type,
		startDate,
		endDate,
		description,
		searchAddress,
		basePoint,
		workAreas,
		zoneRows,
		lots,
		drivers,
		vehicles,
		operators,
		shifts,
		defaultShift,
		warehouseId,
		busParams,
		serviceTimes,
		preProcessingParameters,
		functionalities,
		smsOptions,
		scannerEmailTemplate,
		loadSequenceType,
		smsTestModePhoneList,
		containerSizes,
		globalSettings,
		externalDelivery,
		dispatch,
		id,
		contractId,
		applyContractData,
	])

	const doSearchAddress = useCallback(async (address: any) => {
		if (!address) return
		try {
			Geocode.setApiKey(process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "")
			Geocode.fromAddress(address).then(
				(response: any) => {
					const { lat, lng } = response.results[0].geometry.location
					setBasePoint({
						type: "Point",
						coordinates: [lng, lat],
					})
					setHasUnsavedFields(true)
				},
				(error: any) => {
					console.error(error)
				}
			)
		} catch (err) {
			console.log("Error retrieving address information")
		}
	}, [])

	// Geocode the Uber warehouse-address that overwrites the contract's address and coordinates for Uber deliveries
	const doSearchUberWarehouseAddress = useCallback(
		async (partnerKey: string, address: string) => {
			if (!address) return
			try {
				Geocode.setApiKey(process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "")
				const response = await Geocode.fromAddress(address)
				const { lat, lng } = response.results[0].geometry.location
				setExternalDelivery((prev: any) => ({
					...prev,
					[partnerKey]: {
						...prev?.[partnerKey],
						warehouseAddress: { address, location: { lat, lng } },
					},
				}))
				setHasUnsavedFields(true)
			} catch (err) {
				console.log("Error retrieving warehouse address information")
			}
		},
		[]
	)

	const submitAttachment = useCallback(async () => {
		if (!tempAttachment?.file) {
			return notification.warn({
				message: Strings.businesses.contracts,
				description: Strings.contracts.attachmentMandatory,
				placement: "bottomRight",
				duration: 5,
			})
		}

		if (!translate(tempAttachment.description)) {
			return notification.warn({
				message: Strings.businesses.contracts,
				description: Strings.contracts.descriptionMandatory,
				placement: "bottomRight",
				duration: 5,
			})
		}

		dispatch(setLoader(true))

		try {
			const request = tempAttachment?._id ? API.put : API.post

			const body = new FormData()
			body.append("file", tempAttachment.file)
			body.append("description", JSON.stringify(tempAttachment.description))
			body.append("filesToDelete", JSON.stringify(filesToDelete))

			if (tempAttachment?._id) {
				body.append("_id", tempAttachment?._id)
			}

			const response = await request({
				url: Endpoints.uriBusinesses(
					`${id}/contracts/${contractId}/attachments/${tempAttachment?._id || ""}`
				),
				data: body,
			})

			if (response.ok) {
				const { contract: contractData } = response.data.results || {}
				applyContractData(contractData)
				setTempAttachment(null)
				setShowAttachmentsDrawer(false)

				notification.success({
					message: Strings.contracts.attachments,
					description: response?.data?.message,
					placement: "bottomRight",
					duration: 5,
				})
			} else {
				notification.error({
					message: Strings.contracts.attachments,
					description:
						response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}

		dispatch(setLoader(false))
	}, [
		tempAttachment,
		filesToDelete,
		dispatch,
		id,
		contractId,
		applyContractData,
	])

	const deleteAttachment = useCallback(
		async (attachmentId: string) => {
			dispatch(setLoader(true))

			try {
				const response = await API.delete({
					url: Endpoints.uriBusinesses(
						`${id}/contracts/${contractId}/attachments/${attachmentId}`
					),
				})

				if (response.ok) {
					const { contract: contractData } = response.data.results || {}
					applyContractData(contractData)

					notification.success({
						message: Strings.contracts.attachments,
						description: response?.data?.message,
						placement: "bottomRight",
						duration: 5,
					})
				} else {
					notification.error({
						message: Strings.contracts.attachments,
						description:
							response?.data?.message || Strings.serverErrors.wentWrong,
						placement: "bottomRight",
						duration: 5,
					})
				}
			} catch (err) {
				notification.error({
					message: Strings.serverErrors.title,
					description: Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			}

			dispatch(setLoader(false))
		},
		[dispatch, id, contractId, applyContractData]
	)

	const markUnsaved = useCallback(() => {
		setHasUnsavedFields(true)
	}, [])

	// Mount: fetch data and set title
	useEffect(() => {
		dispatch(setTitle(contract?.description || ""))
		getData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Subscribe to messaging groups so the Uber channel can be picked from a dropdown.
	useEffect(() => {
		const unsubscribe = watchGroups(setMessagingGroups)
		return () => unsubscribe()
	}, [])

	// Breadcrumb setup
	useEffect(() => {
		dispatch(updateCrumb())

		if (contractId === "new") {
			dispatch(setTitle(Strings.contracts.new))
		} else if (contract) {
			const string = contract?.type?.name + " "
			let date = ""

			if (contract.startDate && contract.endDate) {
				date = `${moment.utc(contract.startDate).format("DD/MM/YYYY")} - ${moment
					.utc(contract.endDate)
					.format("DD/MM/YYYY")}`
			}

			dispatch(setTitle(`${string}${date ? ` (${date})` : ""}` || ""))
		}

		delayedDispatch(
			setBreadcrumb(() => {
				const string = contract?.type?.name + " "
				let date = ""

				if (contract?.startDate && contract?.endDate) {
					date = `${moment.utc(contract?.startDate).format("DD/MM/YYYY")} - ${moment
						.utc(contract?.endDate)
						.format("DD/MM/YYYY")}`
				}

				return {
					locations: [
						{
							icon: "partner",
							text: Strings.sidebar.businesses,
							route: "/businesses",
						},
						{
							icon: "partner",
							text: business?.name,
							route: `/businesses/${id}`,
						},
						{
							text:
								contractId === "new"
									? Strings.contracts.new
									: `${string}${date ? ` (${date})` : ""}`,
							icon: contractId === "new" ? "plus" : "pencil-outline",
						},
					],
					actions: [
						{
							type: "switch",
							value: isActive,
							onClick: (value: boolean) => {
								setIsActive(value)
								setHasUnsavedFields(true)
							},
							text: Strings.generic.active,
							small: {
								label: true,
								switch: true,
							},
						},
						{
							type: "button",
							text: Strings.generic.save,
							disabled: !hasUnsavedFields,
							className: "BreadcrumbSaveButton",
							onClick: () => saveContract(),
							separator: "left",
							isSave: true,
						},
					],
				}
			})
		)
	}, [
		contract,
		business,
		id,
		contractId,
		isActive,
		hasUnsavedFields,
		saveContract,
		dispatch,
	])

	// --- Render helpers ---

	const renderGeneralInformation = () => (
		<ContentWrapper>
			<h2 className="TitleSeparator">{Strings.generic.generalInformation}</h2>
			<Row gutter={[20, 10]}>
				<Col xs={24} md={8}>
					<label htmlFor="contract_name" className="InputLabel">
						{Strings.contracts.name}
					</label>
					<Input
						id="contract_name"
						style={{ resize: "none" }}
						placeholder={Strings.contracts.name}
						value={name || ""}
						onChange={(e: any) => {
							setName(e.target.value)
							markUnsaved()
						}}
					/>
				</Col>
				<Col xs={24} md={8}>
					<label htmlFor="business_name" className="InputLabel">
						{Strings.contracts.business}
					</label>
					<Input id="business_name" value={business?.name || "-"} readOnly />
				</Col>
				<Col xs={24} md={8}>
					<label
						htmlFor="contract_type"
						className="InputLabel --label-required"
					>
						{Strings.contracts.type}
					</label>
					<Select
						id="contract_type"
						style={{ width: "100%" }}
						placeholder={Strings.contracts.type}
						showSearch
						disabled={false}
						filterOption={(input: any, option: any) =>
							option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
						}
						value={type?.name || null}
						onChange={(elem: any) => {
							setType(defaultContractTypes.find((ct: any) => ct._id === elem))
							markUnsaved()
						}}
					>
						{defaultContractTypes?.map((elem: any) => (
							<Select.Option value={elem._id}>{elem.name}</Select.Option>
						))}
					</Select>
				</Col>
				<Col xs={24} md={12}>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
						}}
					>
						<label htmlFor="contract_description" className="InputLabel">
							{Strings.fields.description}
						</label>
						<Input.TextArea
							id="contract_description"
							style={{ resize: "none", height: "100%" }}
							placeholder={Strings.fields.description}
							value={description || ""}
							onChange={(e: any) => {
								setDescription(e.target.value)
								markUnsaved()
							}}
							rows={4}
						/>
					</div>
				</Col>
				<Col xs={24} md={12}>
					<Row gutter={[20, 10]}>
						<Col xs={24} md={12}>
							<label
								htmlFor="contract_start_date"
								className="GenericLabel --label-required"
							>
								{Strings.fields.startDate}
							</label>
							<DatePicker
								id="contract_start_date"
								style={{ width: "100%" }}
								placeholder={Strings.contracts.startDate}
								value={(startDate && moment.utc(startDate)) || null}
								format="DD-MM-YYYY"
								inputReadOnly
								onChange={(value: any) => {
									setStartDate(value)
									markUnsaved()
								}}
							/>
						</Col>
						<Col xs={24} md={12}>
							<label
								htmlFor="contract_end_date"
								className="GenericLabel --label-required"
							>
								{Strings.fields.endDate}
							</label>
							<DatePicker
								id="contract_end_date"
								style={{ width: "100%" }}
								placeholder={Strings.contracts.endDate}
								value={(endDate && moment.utc(endDate)) || null}
								format="DD-MM-YYYY"
								inputReadOnly
								onChange={(value: any) => {
									setEndDate(value)
									markUnsaved()
								}}
							/>
						</Col>
						<Col xs={24} md={12}>
							<label
								htmlFor="contract_load_sequence_type"
								className="GenericLabel"
							>
								{Strings.fields.loadSequenceType}
							</label>

							<Select
								id="contract_load_sequence_type"
								style={{ width: "100%" }}
								disabled={false}
								value={loadSequenceType || ""}
								onChange={(elem: any) => {
									setLoadSequenceType(elem)
									markUnsaved()
								}}
							>
								{["grid", "pile"].map((elem: any) => (
									<Select.Option value={elem}>
										{elem.charAt(0).toUpperCase() + elem.slice(1).toLowerCase()}
									</Select.Option>
								))}
							</Select>
						</Col>
					</Row>
				</Col>
			</Row>
		</ContentWrapper>
	)

	const renderLocation = () => (
		<ContentWrapper>
			<h2 style={{ marginBottom: 20 }} className="TitleSeparator">
				{Strings.contracts.baseLocation}
			</h2>
			<Row gutter={[20, 10]}>
				<Col xs={24}>
					<Map
						location={
							(basePoint?.coordinates?.length && {
								lat: basePoint?.coordinates?.[1],
								lng: basePoint?.coordinates?.[0],
							}) ||
							{}
						}
						parent={{
							setState: (updater: any) => {
								if (typeof updater === "function") {
									// Map component calls parent.setState with location updates
									const result = updater({})
									if (result?.basePoint) {
										setBasePoint(result.basePoint)
										markUnsaved()
									}
								} else if (updater?.basePoint) {
									setBasePoint(updater.basePoint)
									markUnsaved()
								}
							},
							state: { basePoint },
						}}
						editingStyle={null}
						editingLocation="location"
						defaultCenter={{ lat: 38.72152, lng: -9.142161 }}
						center={
							(basePoint?.coordinates?.length && {
								lat: basePoint?.coordinates?.[1],
								lng: basePoint?.coordinates?.[0],
							}) ||
							null
						}
						isMarkerShown
						zoom={12}
						googleMapURL={`https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing,places&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}
						loadingElement={<div style={{ height: "100%" }} />}
						containerElement={<div style={{ height: "400px" }} />}
						mapElement={<div style={{ height: "100%" }} />}
					/>
				</Col>
				<Col md={18}>
					<label
						htmlFor="contract_search_address"
						className="InputLabel --label-required"
					>
						{Strings.contracts.baseLocationAddress}
					</label>
					<Input
						id="contract_search_address"
						value={searchAddress || ""}
						placeholder={Strings.contracts.baseLocationAddress}
						onChange={(event: any) => {
							const value = event.target.value
							setSearchAddress(value)
							clearTimeout(searchTimerRef.current)
							searchTimerRef.current = setTimeout(() => {
								if (value) doSearchAddress(value)
							}, 500)
						}}
						onBlur={() => {
							if (searchAddress) doSearchAddress(searchAddress)
						}}
						onKeyDown={(e: any) => {
							if (e.key === "Enter") doSearchAddress(searchAddress)
						}}
					/>
				</Col>
				<Col md={6}>
					<label htmlFor="contract_warehouse_id" className="InputLabel">
						{Strings.contracts.warehouseId}
					</label>
					<Input
						id="contract_warehouse_id"
						style={{ resize: "none" }}
						placeholder={Strings.contracts.warehouseId}
						value={warehouseId || ""}
						onChange={(e: any) => {
							setWarehouseId(e.target.value)
							markUnsaved()
						}}
					/>
				</Col>
			</Row>
		</ContentWrapper>
	)

	const renderDrivers = () => (
		<ContentWrapper>
			<div style={{ marginBottom: 20 }} className="MultiTitleSeparator">
				<h2>{Strings.staff.drivers}</h2>
			</div>
			<Select
				id="contract_drivers"
				style={{ width: "100%" }}
				placeholder={Strings.staff.drivers}
				showSearch
				showArrow
				filterOption={(input: any, option: any) =>
					option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
				}
				mode="multiple"
				value={drivers}
				onChange={(elem: any) => {
					setDrivers(elem)
					markUnsaved()
				}}
			>
				{defaultDrivers.map((driver: any) => (
					<Select.Option key={driver._id} value={driver._id}>
						{driver.name}
					</Select.Option>
				))}
			</Select>
		</ContentWrapper>
	)

	const renderVehicles = () => (
		<ContentWrapper>
			<div style={{ marginBottom: 20 }} className="MultiTitleSeparator">
				<h2>{Strings.settings.vehicles}</h2>
			</div>
			<Select
				id="contract_vehicles"
				style={{ width: "100%" }}
				placeholder={Strings.settings.vehicles}
				showSearch
				showArrow
				filterOption={(input: any, option: any) =>
					option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
				}
				mode="multiple"
				value={vehicles}
				onChange={(elem: any) => {
					setVehicles(elem)
					markUnsaved()
				}}
			>
				{defaultVehicles.map((vehicle: any) => (
					<Select.Option key={vehicle._id} value={vehicle._id}>
						{`${vehicle.name} (${vehicle.licensePlate})`}
					</Select.Option>
				))}
			</Select>
		</ContentWrapper>
	)

	const renderOperators = () => (
		<ContentWrapper>
			<div style={{ marginBottom: 20 }} className="MultiTitleSeparator">
				<h2>{Strings.staff.operators}</h2>
			</div>
			<Select
				id="contract_operators"
				style={{ width: "100%" }}
				placeholder={Strings.staff.operators}
				showSearch
				showArrow
				filterOption={(input: any, option: any) =>
					option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
				}
				mode="multiple"
				value={operators}
				onChange={(elem: any) => {
					setOperators(elem)
					markUnsaved()
				}}
			>
				{defaultOperators.map((operator: any) => (
					<Select.Option key={operator._id} value={operator._id}>
						{operator.name}
					</Select.Option>
				))}
			</Select>
		</ContentWrapper>
	)

	const renderShifts = () => (
		<ContentWrapper>
			<div style={{ marginBottom: 20 }} className="MultiTitleSeparator">
				<h2>{Strings.fields.shift}</h2>
			</div>
			<Row gutter={[20, 10]}>
				<Col xs={24} md={18}>
					<label className="InputLabel">{Strings.fields.shift}</label>
					<Select
						id="contract_shifts"
						style={{ width: "100%" }}
						placeholder={Strings.fields.shift}
						showSearch
						showArrow
						filterOption={(input: any, option: any) =>
							option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
						}
						mode="multiple"
						value={shifts}
						onChange={(elem: any) => {
							setShifts(elem)
							markUnsaved()
						}}
					>
						{defaultShifts.map((shift: any) => (
							<Select.Option key={shift._id} value={shift._id}>
								{shift.name}
							</Select.Option>
						))}
					</Select>
				</Col>
				<Col xs={24} md={6}>
					<label className="InputLabel">
						{`${Strings.fields.defaultShift}`}
					</label>
					<Select
						id="contract_default_shifts"
						style={{ width: "100%" }}
						placeholder={`${Strings.fields.defaultShift}`}
						showSearch
						showArrow
						allowClear
						filterOption={(input: any, option: any) =>
							option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
						}
						value={defaultShift || null}
						onChange={(elem: any) => {
							setDefaultShift(elem)
							markUnsaved()
						}}
					>
						{shifts.map((shiftId: string) => {
							const shift = defaultShifts.find((s: any) => s._id === shiftId)
							return shift ? (
								<Select.Option key={shift._id} value={shift._id}>
									{shift.name}
								</Select.Option>
							) : null
						})}
					</Select>
				</Col>
			</Row>
		</ContentWrapper>
	)

	const renderWorkAreasList = () => {
		if (!Array.isArray(workAreas) || workAreas.length === 0) {
			return null
		}

		return (
			<Row gutter={[30, 10]}>
				{workAreas.map((workArea: any, index: number) => {
					let startValue = workArea.start || ""
					let endValue = workArea.end || ""

					if (startValue.length > 4) {
						startValue = `${startValue.substring(0, 4)}-${startValue.substring(4)}`
					}

					if (endValue.length > 4) {
						endValue = `${endValue.substring(0, 4)}-${endValue.substring(4)}`
					}

					return (
						<Col key={`work_area_${index}`} xs={24} md={12}>
							<div className="WorkareaEntryBlock">
								<div>
									<label
										htmlFor={`contract_work_area_${index}_start`}
										className="InputLabel"
									>
										{Strings.contracts.startPostcode}
									</label>
									<Input
										id={`contract_work_area_${index}_start`}
										value={startValue}
										style={{ width: "100%" }}
										placeholder="1000-001"
										onChange={(e: any) => {
											const value = e.target.value.replace("-", "")
											if (isNaN(value) || value?.length > 7) return

											const newWorkAreas = [...workAreas]
											newWorkAreas[index] = {
												...newWorkAreas[index],
												start: value,
											}
											setWorkAreas(newWorkAreas)
											markUnsaved()
										}}
									/>
								</div>
								<div>
									<label
										htmlFor={`contract_work_area_${index}_end`}
										className="InputLabel"
									>
										{Strings.contracts.endPostcode}
									</label>
									<Input
										id={`contract_work_area_${index}_end`}
										value={endValue}
										style={{ width: "100%" }}
										placeholder="2100-500"
										onChange={(e: any) => {
											const value = e.target.value.replace("-", "")
											if (isNaN(value) || value?.length > 7) return

											const newWorkAreas = [...workAreas]
											newWorkAreas[index] = {
												...newWorkAreas[index],
												end: value,
											}
											setWorkAreas(newWorkAreas)
											markUnsaved()
										}}
									/>
								</div>
								<div className="WorkareaDeleteContainer">
									<button
										className="WorkareaDeleteButton"
										onClick={() => {
											const newWorkAreas = [...workAreas]
											newWorkAreas.splice(index, 1)
											setWorkAreas(newWorkAreas)
											markUnsaved()
										}}
									>
										<Icon name="trash" />
									</button>
								</div>
							</div>
						</Col>
					)
				})}
			</Row>
		)
	}

	// Fetch the contract's geojson library.
	const fetchAvailableZones = useCallback(async () => {
		if (!contractId || contractId === "new") return
		try {
			const response = await API.get({
				url: Endpoints.uriGeojsonZones(`?contractId=${contractId}`),
			})
			if (response.ok) {
				setAvailableZones(response.data.results || [])
			}
		} catch (err) {
			// Non-fatal: the section still works for creating new zones.
		}
	}, [contractId])

	useEffect(() => {
		fetchAvailableZones()
	}, [fetchAvailableZones])

	// Create a new geojson (paste/upload/draw), or save an edit to an existing one.
	// A created zone is NOT auto-selected — the user picks its mode in the list.
	const handleSaveZone = useCallback(
		async (payload: ZonePayload): Promise<boolean> => {
			const editingId = zoneToEdit?._id
			try {
				const response = editingId
					? await API.put({
							url: Endpoints.uriGeojsonZones(editingId),
							data: payload,
						})
					: await API.post({
							url: Endpoints.uriGeojsonZones(),
							data: { contract: contractId, ...payload },
						})

				if (response.ok) {
					const zone = response.data.results
					setAvailableZones((prev) =>
						editingId
							? prev.map((z: any) => (z._id === editingId ? zone : z))
							: [zone, ...prev]
					)
					notification.success({
						message: Strings.contracts.geojsonZones,
						description: editingId
							? Strings.contracts.geojsonUpdated
							: Strings.contracts.geojsonCreated,
						placement: "bottomRight",
						duration: 5,
					})
					return true
				}
				notification.error({
					message: Strings.contracts.geojsonZones,
					description: response.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
				return false
			} catch (err) {
				notification.error({
					message: Strings.contracts.geojsonZones,
					description: Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
				return false
			}
		},
		[contractId, zoneToEdit]
	)

	// Delete a geojson from the library (also drops every row using it). The API
	// refuses to delete a global zone other contracts still reference.
	const handleDeleteZone = useCallback(async (zoneId: string) => {
		try {
			const response = await API.delete({
				url: Endpoints.uriGeojsonZones(zoneId),
			})
			if (response.ok) {
				setAvailableZones((prev) => prev.filter((z: any) => z._id !== zoneId))
				setZoneRows((prev) => prev.filter((row) => row.zone !== zoneId))
				setZoneToDelete(null)
				setZoneDeleteError(null)
			} else {
				setZoneDeleteError(
					response.data?.message || Strings.serverErrors.wentWrong
				)
			}
		} catch (err) {
			setZoneDeleteError(Strings.serverErrors.wentWrong)
		}
	}, [])

	const updateZoneRows = (next: ContractZoneRow[]) => {
		setZoneRows(next)
		markUnsaved()
	}

	const zoneRowKey = (row: ContractZoneRow) => `${row.zone}|${row.mode}`

	const toggleZoneVisibility = (row: ContractZoneRow) => {
		const key = zoneRowKey(row)
		setHiddenZoneKeys((prev) =>
			prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
		)
	}

	// A zone may be a delivery area and carry one vehicle mode, but never both
	// vehicle modes, and never the same mode twice.
	const isModeTaken = (
		rows: ContractZoneRow[],
		index: number,
		mode: ZoneMode
	) => {
		// A row with no zone picked yet constrains nothing.
		const zone = rows[index]?.zone
		if (!zone) return false

		return rows.some((row, i) => {
			if (i === index || row.zone !== zone) return false
			return mode === "deliveryArea"
				? row.mode === "deliveryArea"
				: row.mode !== "deliveryArea"
		})
	}

	// One list of (zone, mode) rows covering delivery-area validation and the
	// van/bike import filters, plus a single map of every configured zone.
	const renderContractZones = () => {
		const zoneById = (id: string) =>
			availableZones.find((z: any) => z._id === id)

		// The contract's own zones are the usual pick, so they come first; the
		// partition is stable, keeping the API's order inside each group.
		const selectableZones = [
			...availableZones.filter((z: any) => !z.isGlobal),
			...availableZones.filter((z: any) => z.isGlobal),
		]

		const configuredRows = zoneRows.filter(
			(row) => zoneById(row.zone) && !hiddenZoneKeys.includes(zoneRowKey(row))
		)

		// One set of polygons for every visible zone, coloured by mode.
		const combinedPolygons = configuredRows.flatMap((row) => {
			const color = ZONE_MODE_COLORS[row.mode]
			return geojsonToPolygonPaths(zoneById(row.zone)?.geojson).map((path) => ({
				paths: path,
				fillColor: color,
				fillOpacity: 0.15,
				strokeColor: color,
				strokeOpacity: 1,
				strokeWeight: 2.5,
			}))
		})

		// Fit the single map to the bounds of every configured zone.
		let boundingBox:
			| { south: number; west: number; north: number; east: number }
			| undefined
		const allPoints = combinedPolygons.flatMap((p) => p.paths)
		if (allPoints.length > 0) {
			boundingBox = {
				south: Math.min(...allPoints.map((pt) => pt.lat)),
				north: Math.max(...allPoints.map((pt) => pt.lat)),
				west: Math.min(...allPoints.map((pt) => pt.lng)),
				east: Math.max(...allPoints.map((pt) => pt.lng)),
			}
		}

		return (
			<ContentWrapper>
				<div style={{ marginBottom: 10 }} className="MultiTitleSeparator">
					<h2>
						{Strings.contracts.geojsonContractZonesTitle}
						<Tooltip
							placement="top"
							title={
								<div>
									<div>{Strings.contracts.geojsonModesLegend}</div>
									{ZONE_MODES.map((mode) => (
										<div key={mode} style={{ marginTop: 10 }}>
											<strong>{Strings.contracts.geojsonModes[mode]}</strong>
											{" — "}
											{Strings.contracts.geojsonModesHelp[mode]}
										</div>
									))}
								</div>
							}
						>
							<QuestionCircleOutlined
								style={{
									marginLeft: 6,
									color: "#999",
									cursor: "help",
									fontSize: "14px",
								}}
							/>
						</Tooltip>
					</h2>
					<button
						onClick={() =>
							updateZoneRows([
								...zoneRows,
								{ zone: "", mode: "deliveryArea" as ZoneMode },
							])
						}
					>
						<Icon name="plus" />
					</button>
				</div>

				{zoneRows.length === 0 ? (
					<p style={{ color: "#888" }}>{Strings.contracts.geojsonNoZones}</p>
				) : (
					zoneRows.map((row, index) => (
						<Row
							gutter={[12, 10]}
							key={`${row.zone}-${row.mode}-${index}`}
							style={{ marginBottom: 10 }}
							align="middle"
						>
							<Col xs={24} md={12}>
								<Select
									style={{ width: "100%" }}
									placeholder={
										Strings.contracts.geojsonDeliveryZonesPlaceholder
									}
									value={row.zone || undefined}
									onChange={(value: string) =>
										updateZoneRows(
											zoneRows.map((r, i) =>
												i === index ? { ...r, zone: value } : r
											)
										)
									}
									showSearch
									optionFilterProp="children"
									notFoundContent={Strings.contracts.geojsonNoAvailable}
								>
									{selectableZones.map((zone: any) => (
										<Select.Option key={zone._id} value={zone._id}>
											{zone.name}
											{zone.isGlobal
												? ` (${Strings.contracts.geojsonGlobal})`
												: ""}
										</Select.Option>
									))}
								</Select>
							</Col>

							<Col xs={16} md={8}>
								<Select
									style={{ width: "100%" }}
									value={row.mode}
									onChange={(value: ZoneMode) =>
										updateZoneRows(
											zoneRows.map((r, i) =>
												i === index ? { ...r, mode: value } : r
											)
										)
									}
								>
									{ZONE_MODES.map((mode) => (
										<Select.Option
											key={mode}
											value={mode}
											disabled={isModeTaken(zoneRows, index, mode)}
										>
											<span
												style={{
													width: 10,
													height: 10,
													borderRadius: 2,
													background: ZONE_MODE_COLORS[mode],
													display: "inline-block",
													marginRight: 8,
												}}
											/>
											{Strings.contracts.geojsonModes[mode]}
										</Select.Option>
									))}
								</Select>
							</Col>

							<Col xs={4} md={2} style={{ textAlign: "center" }}>
								<Tooltip
									placement="top"
									title={
										hiddenZoneKeys.includes(zoneRowKey(row))
											? Strings.contracts.geojsonShowOnMap
											: Strings.contracts.geojsonHideFromMap
									}
								>
									<Button
										type="text"
										disabled={!row.zone}
										onClick={() => toggleZoneVisibility(row)}
										icon={
											hiddenZoneKeys.includes(zoneRowKey(row)) ? (
												<EyeInvisibleOutlined style={{ color: "#999" }} />
											) : (
												<EyeOutlined />
											)
										}
									/>
								</Tooltip>
							</Col>

							<Col xs={4} md={2}>
								<Button
									type="text"
									danger
									onClick={() =>
										updateZoneRows(zoneRows.filter((_, i) => i !== index))
									}
								>
									{Strings.contracts.geojsonRemove}
								</Button>
							</Col>
						</Row>
					))
				)}

				{combinedPolygons.length > 0 && (
					<GoogleMaps
						polygons={combinedPolygons}
						boundingBox={boundingBox}
						mapContainerStyle={{
							width: "100%",
							height: "360px",
							borderRadius: 5,
						}}
					/>
				)}
			</ContentWrapper>
		)
	}

	// The geojson library (create / edit / list / delete): the contract's own zones
	// plus the global ones. The zone list above is a selection of these.
	const renderAvailableGeojsons = () => (
		<ContentWrapper>
			<div style={{ marginBottom: 10 }} className="MultiTitleSeparator">
				<h2>{Strings.contracts.geojsonLibraryTitle}</h2>
				<button
					onClick={() => {
						setZoneToEdit(null)
						setShowZoneModal(true)
					}}
				>
					<Icon name="plus" />
				</button>
			</div>

			{availableZones.length === 0 ? (
				<p style={{ color: "#888" }}>{Strings.contracts.geojsonNoAvailable}</p>
			) : (
				<Row gutter={[16, 12]}>
					{availableZones.map((zone: any) => (
						<Col xs={24} md={12} key={zone._id}>
							<div
								style={{
									border: "1px solid #eee",
									borderRadius: 6,
									padding: "8px 12px",
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
								}}
							>
								<div>
									<strong>{zone.name}</strong>
									{zone.isGlobal && (
										<span style={{ color: "#722ED1", marginLeft: 8 }}>
											• {Strings.contracts.geojsonGlobal}
										</span>
									)}
									<span style={{ color: "#888", marginLeft: 8 }}>
										{Strings.contracts.geojsonBuffer}: {zone.buffer ?? 0}km
									</span>
									{zoneRows.some((row) => row.zone === zone._id) && (
										<span style={{ color: "#1C70D9", marginLeft: 8 }}>
											• {Strings.contracts.geojsonInUse}
										</span>
									)}
								</div>
								<div>
									<Button
										type="text"
										onClick={() => {
											setZoneToEdit(zone)
											setShowZoneModal(true)
										}}
									>
										{Strings.contracts.geojsonEdit}
									</Button>
									<Button
										type="text"
										danger
										onClick={() => {
											setZoneDeleteError(null)
											setZoneToDelete(zone)
										}}
									>
										{Strings.contracts.geojsonRemove}
									</Button>
								</div>
							</div>
						</Col>
					))}
				</Row>
			)}

			<ZoneModal
				visible={showZoneModal}
				zone={zoneToEdit}
				onClose={() => {
					setShowZoneModal(false)
					setZoneToEdit(null)
				}}
				onSubmit={handleSaveZone}
			/>

			<ZoneDeleteModal
				visible={!!zoneToDelete}
				zone={zoneToDelete}
				inUse={
					!!zoneToDelete &&
					zoneRows.some((row) => row.zone === zoneToDelete._id)
				}
				error={zoneDeleteError}
				onClose={() => {
					setZoneToDelete(null)
					setZoneDeleteError(null)
				}}
				onConfirm={async () => {
					if (!zoneToDelete) return
					await handleDeleteZone(zoneToDelete._id)
				}}
			/>
		</ContentWrapper>
	)

	const renderWorkAreas = () => (
		<ContentWrapper>
			<div style={{ marginBottom: 10 }} className="MultiTitleSeparator">
				<h2>{Strings.contracts.workAreas}</h2>
				<button
					onClick={() => {
						if (canAddWorkArea()) {
							setWorkAreas([...workAreas, { start: null, end: null }])
						}
					}}
				>
					<Icon name="plus" />
				</button>
			</div>
			{renderWorkAreasList()}
		</ContentWrapper>
	)

	const renderLots = () => (
		<ContentWrapper>
			<div style={{ marginBottom: 10 }} className="MultiTitleSeparator">
				<h2>{Strings.contracts.lots}</h2>
			</div>
			<Row gutter={[30, 10]}>
				<Col xs={24}>
					<Select
						id="contract_lots"
						mode="tags"
						style={{ width: "100%" }}
						placeholder={Strings.contracts.lotsPlaceholder}
						value={lots || []}
						onChange={(value: string[]) => {
							setLots(value)
							markUnsaved()
						}}
						tokenSeparators={[","]}
					/>
				</Col>
			</Row>
		</ContentWrapper>
	)

	const renderFunctionalities = () => {
		const functionalitiesArray = [
			{
				label: Strings.contracts.allowCheckOut,
				defaultValue: functionalities?.allowCheckOut,
				key: "allowCheckOut",
				helperText: Strings.contracts.allowCheckOutHelper,
			},
			{
				label: Strings.contracts.onTransitSms,
				defaultValue: functionalities?.onTransitSms,
				key: "onTransitSms",
				helperText: Strings.contracts.onTransitSmsHelper,
			},
			{
				label: Strings.contracts.smsTestMode,
				defaultValue: functionalities?.smsTestMode,
				key: "smsTestMode",
				helperText: Strings.contracts.smsTestModeHelper,
			},
			{
				label: Strings.contracts.useAddress,
				defaultValue: functionalities?.useAddress,
				key: "useAddress",
				helperText: Strings.contracts.useAddressHelper,
			},
			{
				label: Strings.contracts.getDeliveryOptions,
				defaultValue: functionalities?.getDeliveryOptions,
				key: "getDeliveryOptions",
				helperText: Strings.contracts.getDeliveryOptionsHelper,
			},
			{
				label: Strings.contracts.beforeDeliverySms,
				defaultValue: functionalities?.beforeDeliverySms,
				key: "beforeDeliverySms",
				helperText: Strings.contracts.beforeDeliverySmsHelper,
			},
			{
				label: Strings.contracts.requiredSignature,
				defaultValue: functionalities?.requiredSignature,
				key: "requiredSignature",
				helperText: Strings.contracts.requiredSignatureHelper,
			},
			{
				label: Strings.contracts.showUntreatedBoxes,
				defaultValue: functionalities?.showUntreatedBoxes,
				key: "showUntreatedBoxes",
				helperText: Strings.contracts.showUntreatedBoxesHelper,
			},
			{
				label: Strings.contracts.separateUntreated,
				defaultValue: functionalities?.separateUntreated,
				key: "separateUntreated",
				helperText: Strings.contracts.separateUntreatedHelper,
			},
			{
				label: Strings.contracts.cancelUntreated,
				defaultValue: functionalities?.cancelUntreated,
				key: "cancelUntreated",
				helperText: Strings.contracts.cancelUntreatedHelper,
			},
			{
				label: Strings.contracts.mergeBikeRoutes,
				defaultValue: functionalities?.mergeBikeRoutes,
				key: "mergeBikeRoutes",
				helperText: Strings.contracts.mergeBikeRoutesHelper,
			},
			{
				label: Strings.contracts.fleetEngine,
				defaultValue: functionalities?.useFleetEngine,
				key: "useFleetEngine",
				helperText: Strings.contracts.fleetEngineHelper,
			},
			{
				label: Strings.contracts.bundles,
				defaultValue: functionalities?.useBundles,
				key: "useBundles",
				helperText: Strings.contracts.useBundlesHelper,
			},
		]

		return (
			<ContentWrapper>
				<div style={{ marginBottom: 20 }} className="MultiTitleSeparator">
					<h2>{Strings.contracts.functionalities}</h2>
				</div>
				<Row gutter={[20, 16]}>
					{functionalitiesArray.map(
						({ label, defaultValue, key, helperText }) => (
							<Col xs={24} sm={12} md={8} key={`switch_key_${label}`}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										padding: "8px 12px",
										border: "1px solid #d9d9d9",
										borderRadius: "6px",
										backgroundColor: "#fafafa",
										minHeight: "56px",
									}}
								>
									<span
										style={{
											display: "flex",
											alignItems: "center",
											flex: 1,
											marginRight: "12px",
											fontSize: "14px",
										}}
									>
										{label}
										{helperText && (
											<Tooltip placement="top" title={helperText}>
												<QuestionCircleOutlined
													style={{
														marginLeft: 6,
														color: "#999",
														cursor: "help",
														fontSize: "14px",
													}}
												/>
											</Tooltip>
										)}
									</span>
									{key === "mergeBikeRoutes" &&
										defaultValue &&
										renderBikeMergeMinTimeSaved()}
									<Switch
										checked={defaultValue}
										size="default"
										onChange={(value: boolean) => {
											setFunctionalities({
												...functionalities,
												[key]: value,
											})
											markUnsaved()
										}}
									/>
								</div>
							</Col>
						)
					)}
				</Row>
			</ContentWrapper>
		)
	}

	const renderSmsOptions = () => {
		if (!functionalities?.onTransitSms && !functionalities?.lastOrder) {
			return null
		}

		return (
			<ContentWrapper>
				<h2 className="TitleSeparator">{Strings.contracts.sms}</h2>
				<Row gutter={[20, 10]}>
					<div style={{ width: "100%" }}>
						<Col xs={24} md={12}>
							<label htmlFor="contract_on_transit_sms" className="InputLabel">
								{Strings.fields.deliverySlotRangeMinutes}
							</label>
							<Input
								id="contract_on_transit_sms_slot_time"
								type="number"
								style={{ resize: "none" }}
								placeholder={Strings.fields.description}
								value={smsOptions?.onTransitSms?.deliverySlotRangeMinutes}
								onChange={(e: any) => {
									const newSmsOptions = { ...smsOptions }
									if (newSmsOptions.onTransitSms) {
										newSmsOptions.onTransitSms = {
											...newSmsOptions.onTransitSms,
											deliverySlotRangeMinutes: e.target.value,
										}
									} else {
										newSmsOptions.onTransitSms = {
											deliverySlotRangeMinutes: e.target.value,
										}
									}
									setSmsOptions(newSmsOptions)
									markUnsaved()
								}}
							/>
						</Col>
						<Col xs={24} md={12} style={{ marginTop: 10 }}>
							<label
								htmlFor="contract_on_transit_sms"
								className="InputLabel"
								style={{ marginRight: 5 }}
							>
								{Strings.fields.template}
							</label>
							<Tooltip
								placement="top"
								title={
									<div>
										<div>{Strings.contracts.templateVariablesLegend}: </div>
										<div style={{ marginTop: 10 }}>
											{"{{from}} - "}
											{Strings.contracts.templateVariablesFrom}
										</div>
										<div>
											{"{{to}} - "}
											{Strings.contracts.templateVariablesTo}
										</div>
										<div>
											{"{{clientName}} - "}
											{Strings.contracts.templateVariablesClientName}
										</div>
										<div>
											{"{{orderId}} - "}
											{Strings.contracts.templateVariablesOrderId}
										</div>
									</div>
								}
							>
								<QuestionCircleOutlined />
							</Tooltip>
							<Input.TextArea
								id="contract_on_transit_sms_template"
								style={{ resize: "none" }}
								placeholder={Strings.fields.description}
								value={smsOptions?.onTransitSms?.template || ""}
								onChange={(e: any) => {
									const newSmsOptions = { ...smsOptions }
									if (newSmsOptions.onTransitSms) {
										newSmsOptions.onTransitSms = {
											...newSmsOptions.onTransitSms,
											template: e.target.value,
										}
									} else {
										newSmsOptions.onTransitSms = { template: e.target.value }
									}
									setSmsOptions(newSmsOptions)
									markUnsaved()
								}}
								rows={4}
							/>
						</Col>
					</div>
				</Row>
			</ContentWrapper>
		)
	}

	const renderSmsTestModeOptions = () => {
		if (!functionalities?.smsTestMode) {
			return null
		}

		return (
			<ContentWrapper>
				<h2 className="TitleSeparator">{Strings.contracts.smsTestMode}</h2>
				<Row gutter={[20, 10]}>
					<Col xs={24} md={12}>
						<label
							htmlFor="contract_scanner_email_recipients"
							className="InputLabel"
						>
							{Strings.fields.phoneNumber}
						</label>
						<Select
							mode="tags"
							style={{ width: "100%" }}
							searchValue={smsTestModePhoneListSearchValue}
							onSearch={(e: string) => {
								setSmsTestModePhoneListSearchValue(e)
							}}
							onChange={(e: Array<string>) => {
								if (lodash.isEqual(e, smsTestModePhoneList)) return

								setSmsTestModePhoneListSearchValue("")
								setSmsTestModePhoneList(e)
								markUnsaved()
							}}
							tokenSeparators={[","]}
							value={smsTestModePhoneList || []}
						/>
					</Col>
				</Row>
			</ContentWrapper>
		)
	}

	const renderScannerEmail = () => {
		if (!functionalities?.scannerEmail) {
			return null
		}

		return (
			<ContentWrapper>
				<h2 className="TitleSeparator">{Strings.contracts.scannerEmail}</h2>
				<Row gutter={[20, 10]}>
					<Col xs={24} md={12}>
						<div>
							<label
								htmlFor="contract_scanner_email_subject"
								className="InputLabel"
							>
								{Strings.fields.subject}
							</label>
							<Input
								id="contract_on_transit_sms_slot_time"
								style={{ resize: "none" }}
								value={scannerEmailTemplate?.subject}
								onChange={(e: any) => {
									setScannerEmailTemplate({
										...scannerEmailTemplate,
										subject: e.target.value,
									})
									markUnsaved()
								}}
							/>
						</div>
						<div>
							<label
								htmlFor="contract_scanner_email_recipients"
								className="InputLabel"
							>
								{Strings.fields.recipients}
							</label>
							<Select
								mode="tags"
								style={{ width: "100%" }}
								searchValue={scannerEmailRecipientsSearchValue}
								onSearch={(e: string) => {
									setScannerEmailRecipientsSearchValue(e)
								}}
								onChange={(e: Array<string>) => {
									const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
									if (e && e.length > 0 && !emailRegex.test(e[e.length - 1])) {
										notification.warn({
											message: Strings.fields.recipients,
											description: Strings.fields.invalidEmail,
											placement: "bottomRight",
											duration: 5,
										})
										return
									}

									if (lodash.isEqual(e, scannerEmailTemplate.recipients)) return

									setScannerEmailRecipientsSearchValue("")
									setScannerEmailTemplate({
										...scannerEmailTemplate,
										recipients: e,
									})
									markUnsaved()
								}}
								tokenSeparators={[","]}
								value={scannerEmailTemplate.recipients || []}
							/>
						</div>
						<div>
							<label htmlFor="contract_scanner_email_cc" className="InputLabel">
								CC
							</label>
							<Select
								mode="tags"
								style={{ width: "100%" }}
								searchValue={scannerEmailCCSearchValue}
								onSearch={(e: string) => {
									setScannerEmailCCSearchValue(e)
								}}
								onChange={(e: Array<string>) => {
									const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
									if (e && e.length > 0 && !emailRegex.test(e[e.length - 1])) {
										notification.warn({
											message: "CC",
											description: Strings.fields.invalidEmail,
											placement: "bottomRight",
											duration: 5,
										})
										return
									}

									if (lodash.isEqual(e, scannerEmailTemplate.cc)) return

									setScannerEmailCCSearchValue("")
									setScannerEmailTemplate({
										...scannerEmailTemplate,
										cc: e,
									})
									markUnsaved()
								}}
								tokenSeparators={[","]}
								value={scannerEmailTemplate.cc || []}
							/>
						</div>
						<div>
							<label
								htmlFor="contract_scanner_email_bcc"
								className="InputLabel"
							>
								BCC
							</label>
							<Select
								mode="tags"
								style={{ width: "100%" }}
								searchValue={scannerEmailBCCSearchValue}
								onSearch={(e: string) => {
									setScannerEmailBCCSearchValue(e)
								}}
								onChange={(e: Array<string>) => {
									const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
									if (e && e.length > 0 && !emailRegex.test(e[e.length - 1])) {
										notification.warn({
											message: "BCC",
											description: Strings.fields.invalidEmail,
											placement: "bottomRight",
											duration: 5,
										})
										return
									}

									if (lodash.isEqual(e, scannerEmailTemplate.bcc)) return

									setScannerEmailBCCSearchValue("")
									setScannerEmailTemplate({
										...scannerEmailTemplate,
										bcc: e,
									})
									markUnsaved()
								}}
								tokenSeparators={[","]}
								value={scannerEmailTemplate.bcc || []}
							/>
						</div>
					</Col>
					<Col xs={24} md={12}>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								height: "100%",
							}}
						>
							<div>
								<label
									htmlFor="contract_scanner_email_body"
									className="InputLabel"
									style={{ marginRight: 5 }}
								>
									{Strings.fields.template}
								</label>
								<Tooltip
									placement="top"
									title={
										<div>
											<div>{Strings.contracts.templateVariablesLegend}: </div>
											<div style={{ marginTop: 10 }}>
												{"{{totalBoxes}} - "}
												{Strings.contracts.templateVariablesTotalBoxes}
											</div>
										</div>
									}
								>
									<QuestionCircleOutlined />
								</Tooltip>
							</div>
							<Input.TextArea
								id="contract_scanner_email_body_text"
								style={{ resize: "none", height: "100%" }}
								placeholder={Strings.fields.template}
								value={scannerEmailTemplate?.body || ""}
								onChange={(e: any) => {
									setScannerEmailTemplate({
										...scannerEmailTemplate,
										body: e.target.value,
									})
									markUnsaved()
								}}
								rows={6}
							/>
						</div>
					</Col>
				</Row>
			</ContentWrapper>
		)
	}

	const renderServiceTime = () => (
		<ContentWrapper>
			<h2 className="TitleSeparator">{Strings.contracts.serviceTime}</h2>
			<Row gutter={[10, 10]}>
				<Col xs={24} md={6}>
					<label htmlFor="contract_service_time_a" className="InputLabel">
						{Strings.fields.serviceTimeA}
					</label>
					<Input
						id="contract_service_time_a"
						value={serviceTimes?.serviceTimeA}
						onChange={(e: any) => {
							const onlyNumbers = e.target.value.replace(/\D/g, "")
							setServiceTimes({
								...serviceTimes,
								serviceTimeA: onlyNumbers,
							})
							markUnsaved()
						}}
					/>
				</Col>
				<Col xs={24} md={6}>
					<label htmlFor="contract_service_time_b" className="InputLabel">
						{Strings.fields.serviceTimeB}
					</label>
					<Input
						id="contract_service_time_b"
						value={serviceTimes?.serviceTimeB}
						onChange={(e: any) => {
							const onlyNumbers = e.target.value.replace(/\D/g, "")
							setServiceTimes({
								...serviceTimes,
								serviceTimeB: onlyNumbers,
							})
							markUnsaved()
						}}
					/>
				</Col>
			</Row>
		</ContentWrapper>
	)

	const renderOptimizerParams = () => (
		<ContentWrapper>
			<div style={{ marginBottom: 10 }} className="MultiTitleSeparator">
				<h2>{Strings.contracts.optimizerParams}</h2>
				<button
					onClick={() => {
						setBusParams({ ...busParams, "": "" })
						markUnsaved()
					}}
				>
					<Icon name="plus" />
				</button>
			</div>
			<Row gutter={[20, 10]} justify="space-between">
				{Object.keys(busParams).map((key, index) => (
					<React.Fragment key={`bus_param_${index}`}>
						<Col xs={24} md={7}>
							<Row gutter={[20, 10]}>
								<Col xs={18}>
									<Input
										style={{ resize: "none" }}
										placeholder="Variable"
										value={key || ""}
										onChange={(e: any) => {
											const newBusParams: Record<string, any> = {}
											Object.keys(busParams).forEach((k) => {
												if (k === key) {
													newBusParams[e.target.value] = busParams[k]
												} else {
													newBusParams[k] = busParams[k]
												}
											})
											setBusParams(newBusParams)
											markUnsaved()
										}}
									/>
								</Col>
								<Col xs={6}>
									<Input
										style={{ resize: "none" }}
										placeholder="Key"
										value={busParams[key] || ""}
										onChange={(e: any) => {
											setBusParams({ ...busParams, [key]: e.target.value })
											markUnsaved()
										}}
									/>
								</Col>
							</Row>
						</Col>
					</React.Fragment>
				))}
			</Row>
		</ContentWrapper>
	)

	const renderPreProcessingParams = () => (
		<ContentWrapper>
			<div
				style={{
					marginBottom: 10,
					display: "flex",
					alignItems: "center",
					gap: 10,
					cursor: "pointer",
				}}
				className="MultiTitleSeparator"
				onClick={() => setPreProcessingCollapsed((prev) => !prev)}
			>
				<h2 style={{ margin: 0 }}>{Strings.contracts.preProcessingParams}</h2>
				<Icon
					name="right-chevron"
					style={{
						transition: "transform 0.2s",
						transform: preProcessingCollapsed
							? "rotate(0deg)"
							: "rotate(90deg)",
						fontSize: 12,
					}}
				/>
			</div>

			{!preProcessingCollapsed && (
				<Row gutter={[20, 10]}>
					{PRE_PROCESSING_PARAM_DEFS.map((def) => {
						const inputId = `contract_pre_processing_${def.key}`
						const currentValue = preProcessingParameters[def.key]
						const label = Strings.contracts[def.labelKey] as string
						const updateValue = (value: any) => {
							const next = { ...preProcessingParameters }
							if (value === "" || value === undefined || value === null) {
								delete next[def.key]
							} else {
								next[def.key] = value
							}
							setPreProcessingParameters(next)
							markUnsaved()
						}

						return (
							<Col xs={24} md={8} key={def.key}>
								<label
									htmlFor={inputId}
									className="InputLabel"
									style={{ marginRight: 5 }}
								>
									{label}
								</label>
								{def.type === "select" ? (
									<>
										<Tooltip
											placement="top"
											title={
												<div style={{ whiteSpace: "pre-line" }}>
													{Strings.contracts[def.helperKey] as string}
												</div>
											}
											color="#000"
											overlayStyle={{ maxWidth: 480 }}
											overlayInnerStyle={{
												backgroundColor: "#000",
												color: "#fff",
												opacity: 1,
											}}
										>
											<QuestionCircleOutlined
												style={{
													marginLeft: 2,
													color: "#999",
													cursor: "help",
												}}
											/>
										</Tooltip>
										<Select
											id={inputId}
											style={{ width: "100%" }}
											value={currentValue ?? def.default}
											onChange={(value: any) => updateValue(value)}
										>
											{def.options.map((option) => (
												<Select.Option key={option.value} value={option.value}>
													{Strings.contracts[option.labelKey] as string}
												</Select.Option>
											))}
										</Select>
									</>
								) : (
									<Input
										id={inputId}
										type="number"
										step={def.type === "int" ? 1 : "any"}
										style={{ resize: "none" }}
										placeholder={String(def.default)}
										value={currentValue ?? ""}
										onChange={(e: any) => updateValue(e.target.value)}
									/>
								)}
							</Col>
						)
					})}
				</Row>
			)}
		</ContentWrapper>
	)

	const renderContainerSizes = () => {
		const bagSizes = containerSizes.bagSizes || []
		const outOfFormatSizes = containerSizes.outOfFormatSizes || []

		return (
			<ContentWrapper>
				<h2 className="TitleSeparator">Container Sizes</h2>

				<div className="row">
					<div className="col-12 col-md-6">
						<div style={{ marginBottom: 30 }}>
							<div style={{ marginBottom: 10 }} className="MultiTitleSeparator">
								<h3
									className="SubtitleSeparator"
									style={{ border: "none", margin: 0 }}
								>
									Bag Sizes
								</h3>
								<div>
									<button
										onClick={() => {
											const currentBagSizes = [...bagSizes]
											const nextNumber = currentBagSizes.length + 1
											currentBagSizes.push({
												name: `#${nextNumber}`,
												size: 0,
												display: "",
											})
											setContainerSizes({
												...containerSizes,
												bagSizes: currentBagSizes,
											})
											markUnsaved()
										}}
										style={{ marginRight: 8 }}
									>
										<Icon name="plus" />
									</button>
									{bagSizes.length > 0 && (
										<button
											onClick={() => {
												const currentBagSizes = [...bagSizes]
												currentBagSizes.pop()
												setContainerSizes({
													...containerSizes,
													bagSizes: currentBagSizes,
												})
												markUnsaved()
											}}
										>
											-
										</button>
									)}
								</div>
							</div>

							{bagSizes.length > 0 &&
								bagSizes.map((size: any, index: number) => (
									<div key={`bag_size_${index}`} className="WorkareaEntryBlock">
										<div>
											<label className="InputLabel">ID</label>
											<Input
												value={size.name}
												readOnly
												style={{
													width: "100%",
													backgroundColor: "#f5f5f5",
												}}
											/>
										</div>
										<div>
											<label className="InputLabel">Value (0-1)</label>
											<Input
												type="number"
												min="0"
												max="1"
												step="0.01"
												value={size.size}
												style={{ width: "100%" }}
												onChange={(e: any) => {
													const value = parseFloat(e.target.value)
													if (value > 0 && value <= 1) {
														const currentBagSizes = [...bagSizes]
														currentBagSizes[index] = {
															...currentBagSizes[index],
															size: value,
														}
														setContainerSizes({
															...containerSizes,
															bagSizes: currentBagSizes,
														})
														markUnsaved()
													}
												}}
											/>
										</div>
										<div>
											<label className="InputLabel">Display</label>
											<Input
												value={size.display}
												style={{ width: "100%" }}
												placeholder="Display text"
												onChange={(e: any) => {
													const currentBagSizes = [...bagSizes]
													currentBagSizes[index] = {
														...currentBagSizes[index],
														display: e.target.value,
													}
													setContainerSizes({
														...containerSizes,
														bagSizes: currentBagSizes,
													})
													markUnsaved()
												}}
											/>
										</div>
									</div>
								))}
						</div>
					</div>
					<div className="col-12 col-md-6">
						<div>
							<div style={{ marginBottom: 10 }} className="MultiTitleSeparator">
								<h3
									className="SubtitleSeparator"
									style={{ border: "none", margin: 0 }}
								>
									Out of Format Sizes
								</h3>
								<div>
									<button
										onClick={() => {
											const currentOutOfFormatSizes = [...outOfFormatSizes]
											const nextNumber = currentOutOfFormatSizes.length + 1
											currentOutOfFormatSizes.push({
												name: `X${nextNumber}X`,
												size: 0,
												display: "",
											})
											setContainerSizes({
												...containerSizes,
												outOfFormatSizes: currentOutOfFormatSizes,
											})
											markUnsaved()
										}}
										style={{ marginRight: 8 }}
									>
										<Icon name="plus" />
									</button>
									{outOfFormatSizes.length > 0 && (
										<button
											onClick={() => {
												const currentOutOfFormatSizes = [...outOfFormatSizes]
												currentOutOfFormatSizes.pop()
												setContainerSizes({
													...containerSizes,
													outOfFormatSizes: currentOutOfFormatSizes,
												})
												markUnsaved()
											}}
										>
											-
										</button>
									)}
								</div>
							</div>

							{outOfFormatSizes.length > 0 &&
								outOfFormatSizes.map((size: any, index: number) => (
									<div
										key={`out_of_format_size_${index}`}
										className="WorkareaEntryBlock"
									>
										<div>
											<label className="InputLabel">ID</label>
											<Input
												value={size.name}
												readOnly
												style={{
													width: "100%",
													backgroundColor: "#f5f5f5",
												}}
											/>
										</div>
										<div>
											<label className="InputLabel">Value</label>
											<Input
												type="number"
												min="0"
												step="1"
												value={size.size}
												style={{ width: "100%" }}
												onChange={(e: any) => {
													const value = parseFloat(e.target.value)
													if (value > 0) {
														const currentOutOfFormatSizes = [
															...outOfFormatSizes,
														]
														currentOutOfFormatSizes[index] = {
															...currentOutOfFormatSizes[index],
															size: value,
														}
														setContainerSizes({
															...containerSizes,
															outOfFormatSizes: currentOutOfFormatSizes,
														})
														markUnsaved()
													}
												}}
											/>
										</div>
										<div>
											<label className="InputLabel">Display</label>
											<Input
												value={size.display}
												style={{ width: "100%" }}
												placeholder="Display text"
												onChange={(e: any) => {
													const currentOutOfFormatSizes = [...outOfFormatSizes]
													currentOutOfFormatSizes[index] = {
														...currentOutOfFormatSizes[index],
														display: e.target.value,
													}
													setContainerSizes({
														...containerSizes,
														outOfFormatSizes: currentOutOfFormatSizes,
													})
													markUnsaved()
												}}
											/>
										</div>
									</div>
								))}
						</div>
					</div>
				</div>
			</ContentWrapper>
		)
	}

	const renderExternalDelivery = () => {
		const partners = [
			{
				key: "uber",
				label: "Uber",
			},
		]

		// Matches the schema default: a partner with no explicit requirement
		// accepts every phone type.
		const ALL_PHONE_TYPES = ["mobile", "landline", "none"]

		return (
			<ContentWrapper>
				<div style={{ marginBottom: 20 }} className="MultiTitleSeparator">
					<h2>{Strings.contracts.externalDelivery}</h2>
					<Tooltip
						placement="top"
						title={Strings.contracts.externalDeliveryHelper}
					>
						<QuestionCircleOutlined
							style={{
								marginLeft: 8,
								color: "#999",
								cursor: "help",
								fontSize: "16px",
							}}
						/>
					</Tooltip>
				</div>
				{partners.map((partner) => {
					const partnerData = externalDelivery?.[partner.key] || {}

					return (
						<div
							key={partner.key}
							style={{
								border: "1px solid #d9d9d9",
								borderRadius: "6px",
								marginBottom: "16px",
								backgroundColor: "#fafafa",
							}}
						>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									padding: "12px 16px",
								}}
							>
								<span
									style={{
										fontSize: "16px",
										fontWeight: 600,
									}}
								>
									{partner.label}
								</span>
								<Switch
									checked={partnerData?.active || false}
									onChange={(value: boolean) => {
										setExternalDelivery({
											...externalDelivery,
											[partner.key]: {
												...externalDelivery?.[partner.key],
												active: value,
											},
										})
										markUnsaved()
									}}
								/>
							</div>
							{partnerData?.active && (
								<div
									style={{
										borderTop: "1px solid #d9d9d9",
										padding: "16px",
									}}
								>
									<Row gutter={[20, 10]}>
										<Col xs={24} md={6}>
											<label className="InputLabel">
												{Strings.contracts.externalDeliveryWarehouseName}
											</label>
											<Input
												id="external_delivery_warehouse_name"
												value={partnerData?.wareHouseName || ""}
												placeholder={
													Strings.contracts.externalDeliveryWarehouseName
												}
												onChange={(e: any) => {
													setExternalDelivery({
														...externalDelivery,
														[partner.key]: {
															...externalDelivery?.[partner.key],
															wareHouseName: e.target.value,
														},
													})
													markUnsaved()
												}}
											/>
										</Col>
										<Col xs={24} md={6}>
											<label className="InputLabel">
												{Strings.contracts.externalDeliveryWarehousePhone}
											</label>
											<Input
												id="external_delivery_warehouse_phone"
												value={partnerData?.warehousePhone || ""}
												placeholder={
													Strings.contracts.externalDeliveryWarehousePhone
												}
												onChange={(e: any) => {
													setExternalDelivery({
														...externalDelivery,
														[partner.key]: {
															...externalDelivery?.[partner.key],
															warehousePhone: e.target.value,
														},
													})
													markUnsaved()
												}}
											/>
										</Col>
										<Col xs={24} md={6}>
											<label className="InputLabel">
												{Strings.contracts.externalDeliveryStoreId}
											</label>
											<Input
												id="external_delivery_store_id"
												value={partnerData?.storeId || ""}
												placeholder={Strings.contracts.externalDeliveryStoreId}
												onChange={(e: any) => {
													setExternalDelivery({
														...externalDelivery,
														[partner.key]: {
															...externalDelivery?.[partner.key],
															storeId: e.target.value.replace(/\s/g, ""),
														},
													})
													markUnsaved()
												}}
											/>
										</Col>
										<Col xs={24} md={6}>
											<label
												htmlFor="external_delivery_barcode_type"
												className="InputLabel"
											>
												{Strings.contracts.externalDeliveryBarcodeType}
											</label>
											<Select
												id="external_delivery_barcode_type"
												style={{ width: "100%" }}
												value={partnerData?.barcodeType || undefined}
												placeholder={
													Strings.contracts.externalDeliveryBarcodeType
												}
												allowClear
												options={[
													{ value: "QR", label: "QR" },
													{ value: "CODE128", label: "CODE128" },
													{ value: "CODE39", label: "CODE39" },
													{
														value: "CODE39_FULL_ASCII",
														label: "CODE39_FULL_ASCII",
													},
													{ value: "EAN13", label: "EAN13" },
												]}
												onChange={(value: string) => {
													setExternalDelivery({
														...externalDelivery,
														[partner.key]: {
															...externalDelivery?.[partner.key],
															barcodeType: value || undefined,
														},
													})
													markUnsaved()
												}}
											/>
										</Col>
									</Row>
									<Row gutter={[24, 12]} style={{ marginTop: 12 }}>
										<Col xs={24} md={12}>
											<label
												htmlFor="external_delivery_accepted_phone_types"
												className="InputLabel"
												style={{ marginRight: 5 }}
											>
												{Strings.contracts.externalDeliveryAcceptedPhoneTypes}
											</label>
											<Tooltip
												placement="top"
												title={
													Strings.contracts
														.externalDeliveryAcceptedPhoneTypesHelper
												}
											>
												<QuestionCircleOutlined />
											</Tooltip>
											<Select
												id="external_delivery_accepted_phone_types"
												mode="multiple"
												style={{ width: "100%" }}
												value={
													partnerData?.requirements?.acceptedPhoneTypes ||
													ALL_PHONE_TYPES
												}
												options={[
													{
														value: "mobile",
														label:
															Strings.contracts.externalDeliveryPhoneTypeMobile,
													},
													{
														value: "landline",
														label:
															Strings.contracts
																.externalDeliveryPhoneTypeLandline,
													},
													{
														value: "none",
														label:
															Strings.contracts.externalDeliveryPhoneTypeNone,
													},
												]}
												onChange={(value: string[]) => {
													setExternalDelivery({
														...externalDelivery,
														[partner.key]: {
															...externalDelivery?.[partner.key],
															requirements: {
																...externalDelivery?.[partner.key]
																	?.requirements,
																acceptedPhoneTypes: value,
															},
														},
													})
													markUnsaved()
												}}
											/>
										</Col>
									</Row>
									<div style={{ marginTop: 12 }}>
										<label className="InputLabel" style={{ marginRight: 5 }}>
											{Strings.contracts.externalDeliveryWarehouseAddress}
										</label>
										<Tooltip
											placement="top"
											title={
												Strings.contracts.externalDeliveryWarehouseAddressHelper
											}
										>
											<QuestionCircleOutlined
												style={{
													color: "#999",
													cursor: "help",
													fontSize: "14px",
												}}
											/>
										</Tooltip>
										<Input
											style={{ marginTop: 4 }}
											value={partnerData?.warehouseAddress?.address || ""}
											placeholder={
												Strings.contracts.externalDeliveryWarehouseAddress
											}
											allowClear
											onChange={(e: any) => {
												const address = e.target.value
												setExternalDelivery({
													...externalDelivery,
													[partner.key]: {
														...externalDelivery?.[partner.key],
														warehouseAddress: address
															? {
																	...externalDelivery?.[partner.key]
																		?.warehouseAddress,
																	address,
																}
															: undefined,
													},
												})
												markUnsaved()
											}}
											onBlur={(e: any) => {
												if (e.target.value)
													doSearchUberWarehouseAddress(
														partner.key,
														e.target.value
													)
											}}
											onPressEnter={(e: any) => {
												if (e.target.value)
													doSearchUberWarehouseAddress(
														partner.key,
														e.target.value
													)
											}}
										/>
										{partnerData?.warehouseAddress?.location?.lat != null &&
											partnerData?.warehouseAddress?.location?.lng != null && (
												<div
													style={{
														fontSize: 12,
														color: "#999",
														marginTop: 4,
													}}
												>
													{partnerData.warehouseAddress.location.lat},{" "}
													{partnerData.warehouseAddress.location.lng}
												</div>
											)}
										{partnerData?.warehouseAddress?.location?.lat != null &&
											partnerData?.warehouseAddress?.location?.lng != null && (
												<Button
													type="link"
													size="small"
													style={{ padding: 0, marginTop: 4 }}
													onClick={() =>
														setShowUberWarehouseMap((prev) => !prev)
													}
												>
													{showUberWarehouseMap
														? Strings.contracts.externalDeliveryWarehouseHideMap
														: Strings.contracts
																.externalDeliveryWarehouseShowMap}
												</Button>
											)}
										{showUberWarehouseMap &&
											partnerData?.warehouseAddress?.location?.lat != null &&
											partnerData?.warehouseAddress?.location?.lng != null && (
												<div style={{ marginTop: 8 }}>
													<Map
														location={{
															lat: partnerData.warehouseAddress.location.lat,
															lng: partnerData.warehouseAddress.location.lng,
														}}
														center={{
															lat: partnerData.warehouseAddress.location.lat,
															lng: partnerData.warehouseAddress.location.lng,
														}}
														isMarkerShown
														zoom={15}
														googleMapURL={`https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing,places&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}
														loadingElement={<div style={{ height: "100%" }} />}
														containerElement={
															<div style={{ height: "300px" }} />
														}
														mapElement={<div style={{ height: "100%" }} />}
													/>
												</div>
											)}
									</div>
									<div style={{ marginTop: 12 }}>
										<label className="InputLabel" style={{ marginRight: 5 }}>
											{Strings.contracts.externalDeliverySmsTemplate}
										</label>
										<Tooltip
											placement="top"
											overlayStyle={{ maxWidth: 300 }}
											title={
												<div>
													<div style={{ marginBottom: 8 }}>
														{Strings.contracts.templateVariablesLegend}:
													</div>
													{[
														{
															variable: "{{orderId}}",
															label: Strings.contracts.templateVariablesOrderId,
														},
														{
															variable: "{{businessName}}",
															label:
																Strings.contracts.templateVariablesBusinessName,
														},
														{
															variable: "{{pincode}}",
															label: "PIN",
														},
													].map((item) => (
														<div
															key={item.variable}
															style={{
																display: "flex",
																alignItems: "center",
																justifyContent: "space-between",
																marginBottom: 4,
															}}
														>
															<span>{item.label}</span>
															<span
																style={{
																	padding: "1px 8px",
																	marginLeft: 8,
																	backgroundColor: "rgba(255,255,255,0.15)",
																	border: "1px solid rgba(255,255,255,0.3)",
																	borderRadius: "4px",
																	cursor: "pointer",
																	fontSize: "12px",
																	userSelect: "none",
																}}
																onClick={() => {
																	const current = partnerData?.smsTemplate || ""
																	const textarea =
																		smsTemplateRef.current?.resizableTextArea
																			?.textArea
																	const pos =
																		textarea?.selectionStart ?? current.length
																	const newValue =
																		current.slice(0, pos) +
																		item.variable +
																		current.slice(pos)
																	setExternalDelivery({
																		...externalDelivery,
																		[partner.key]: {
																			...externalDelivery?.[partner.key],
																			smsTemplate: newValue,
																		},
																	})
																	markUnsaved()
																}}
															>
																{item.variable}
															</span>
														</div>
													))}
												</div>
											}
										>
											<QuestionCircleOutlined
												style={{
													color: "#999",
													cursor: "help",
													fontSize: "14px",
												}}
											/>
										</Tooltip>
										<Input.TextArea
											ref={smsTemplateRef}
											style={{ resize: "none", marginTop: 4 }}
											value={partnerData?.smsTemplate || ""}
											placeholder={
												Strings.contracts.externalDeliverySmsTemplate
											}
											onChange={(e: any) => {
												setExternalDelivery({
													...externalDelivery,
													[partner.key]: {
														...externalDelivery?.[partner.key],
														smsTemplate: e.target.value,
													},
												})
												markUnsaved()
											}}
											rows={3}
										/>
									</div>
									<div style={{ marginTop: 12 }}>
										<label className="InputLabel">
											{Strings.contracts.externalDeliveryPickupNotes}
										</label>
										<Input.TextArea
											style={{ resize: "none", marginTop: 4 }}
											value={partnerData?.pickupNotes || ""}
											placeholder={
												Strings.contracts.externalDeliveryPickupNotes
											}
											onChange={(e: any) => {
												setExternalDelivery({
													...externalDelivery,
													[partner.key]: {
														...externalDelivery?.[partner.key],
														pickupNotes: e.target.value,
													},
												})
												markUnsaved()
											}}
											rows={3}
										/>
									</div>
									<div
										style={{
											marginTop: 16,
											paddingTop: 12,
											borderTop: "1px solid #eee",
										}}
									>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
											}}
										>
											<span
												style={{
													display: "flex",
													alignItems: "center",
													fontSize: 14,
													fontWeight: 600,
												}}
											>
												{Strings.contracts.externalDeliveryMessagingChannel}
												<Tooltip
													placement="top"
													title={
														Strings.contracts
															.externalDeliveryMessagingChannelHelper
													}
												>
													<QuestionCircleOutlined
														style={{
															marginLeft: 6,
															color: "#999",
															cursor: "help",
															fontSize: 14,
														}}
													/>
												</Tooltip>
											</span>
											<Switch
												checked={
													partnerData?.messagingChannel?.enabled || false
												}
												onChange={(value: boolean) => {
													setExternalDelivery({
														...externalDelivery,
														[partner.key]: {
															...externalDelivery?.[partner.key],
															messagingChannel: {
																...externalDelivery?.[partner.key]
																	?.messagingChannel,
																enabled: value,
															},
														},
													})
													markUnsaved()
												}}
											/>
										</div>
										{partnerData?.messagingChannel?.enabled && (
											<div style={{ marginTop: 12 }}>
												<label className="InputLabel">
													{
														Strings.contracts
															.externalDeliveryMessagingChannelGroup
													}
												</label>
												<Select
													showSearch
													style={{ width: "100%" }}
													value={
														partnerData?.messagingChannel?.groupId || undefined
													}
													placeholder={
														Strings.contracts
															.externalDeliveryMessagingChannelGroupPlaceholder
													}
													optionFilterProp="label"
													allowClear
													options={messagingGroups.map((g) => ({
														value: g.id,
														label: g.name,
													}))}
													onChange={(value: string) => {
														const selected = messagingGroups.find(
															(g) => g.id === value
														)
														setExternalDelivery({
															...externalDelivery,
															[partner.key]: {
																...externalDelivery?.[partner.key],
																messagingChannel: {
																	...externalDelivery?.[partner.key]
																		?.messagingChannel,
																	groupId: value || undefined,
																	groupName: selected?.name || undefined,
																},
															},
														})
														markUnsaved()
													}}
												/>
												<div style={{ marginTop: 12 }}>
													<label className="InputLabel">
														{
															Strings.contracts
																.externalDeliveryMessagingChannelEvents
														}
													</label>
													<Row gutter={[16, 8]} style={{ marginTop: 4 }}>
														{[
															{
																key: "enRoutePickup",
																label:
																	Strings.contracts
																		.externalDeliveryEventEnRoutePickup,
															},
															{
																key: "arrivingPickup",
																label:
																	Strings.contracts
																		.externalDeliveryEventArrivingPickup,
															},
															{
																key: "pickupComplete",
																label:
																	Strings.contracts
																		.externalDeliveryEventPickupComplete,
															},
															{
																key: "allDeliveriesDone",
																label:
																	Strings.contracts
																		.externalDeliveryEventAllDeliveriesDone,
															},
															{
																key: "partialReturn",
																label:
																	Strings.contracts
																		.externalDeliveryEventPartialReturn,
															},
															{
																key: "returnsCompleted",
																label:
																	Strings.contracts
																		.externalDeliveryEventReturnsCompleted,
															},
															{
																key: "routeCancelled",
																label:
																	Strings.contracts
																		.externalDeliveryEventRouteCancelled,
															},
														].map((evt) => (
															<Col xs={24} md={12} key={evt.key}>
																<div
																	style={{
																		display: "flex",
																		alignItems: "center",
																		justifyContent: "space-between",
																	}}
																>
																	<span style={{ fontSize: 14 }}>
																		{evt.label}
																	</span>
																	<Switch
																		size="small"
																		checked={
																			partnerData?.messagingChannel?.events?.[
																				evt.key
																			] !== false
																		}
																		onChange={(value: boolean) => {
																			setExternalDelivery({
																				...externalDelivery,
																				[partner.key]: {
																					...externalDelivery?.[partner.key],
																					messagingChannel: {
																						...externalDelivery?.[partner.key]
																							?.messagingChannel,
																						events: {
																							...externalDelivery?.[partner.key]
																								?.messagingChannel?.events,
																							[evt.key]: value,
																						},
																					},
																				},
																			})
																			markUnsaved()
																		}}
																	/>
																</div>
															</Col>
														))}
													</Row>
												</div>
											</div>
										)}
									</div>
									{(["local", "dev", "stag"].includes(
										process.env.ENVIRONMENT || ""
									) ||
										process.env.ENVIRONMENT === undefined ||
										process.env.ENVIRONMENT === null) && (
										<div
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												marginTop: 12,
												padding: "8px 12px",
												border: "1px solid #d9d9d9",
												borderRadius: "6px",
												backgroundColor: "#fff",
											}}
										>
											<span
												style={{
													display: "flex",
													alignItems: "center",
													fontSize: "14px",
												}}
											>
												{Strings.contracts.externalDeliveryTestMode}
												<Tooltip
													placement="top"
													title={
														Strings.contracts.externalDeliveryTestModeHelper
													}
												>
													<QuestionCircleOutlined
														style={{
															marginLeft: 6,
															color: "#999",
															cursor: "help",
															fontSize: "14px",
														}}
													/>
												</Tooltip>
											</span>
											<Switch
												checked={partnerData?.testMode || false}
												onChange={(value: boolean) => {
													setExternalDelivery({
														...externalDelivery,
														[partner.key]: {
															...externalDelivery?.[partner.key],
															testMode: value,
														},
													})
													markUnsaved()
												}}
											/>
										</div>
									)}
								</div>
							)}
						</div>
					)
				})}
			</ContentWrapper>
		)
	}

	const renderGlobalSettings = () => (
		<ContentWrapper>
			<h2 className="TitleSeparator">Global Settings</h2>
			<Row gutter={[20, 10]}>
				<Col xs={24} md={6}>
					<label htmlFor="contract_patch_address_radius" className="InputLabel">
						Patch Address Radius (km)
					</label>
					<Input
						id="contract_patch_address_radius"
						type="number"
						min="0"
						step="0.1"
						value={globalSettings?.patchAddressRadiusKm || 0}
						onChange={(e: any) => {
							const value = parseFloat(e.target.value) || 0
							setGlobalSettings({
								...globalSettings,
								patchAddressRadiusKm: value,
							})
							markUnsaved()
						}}
						placeholder="0.0"
					/>
				</Col>
				<Col xs={24} md={6}>
					<label
						htmlFor="contract_import_file_identifier"
						className="InputLabel"
					>
						Import File Identifier
					</label>
					<Input
						id="contract_import_file_identifier"
						value={globalSettings?.importFileIdentifier || ""}
						onChange={(e: any) => {
							const value = e.target.value.replace(/\s+/g, "")
							setGlobalSettings({
								...globalSettings,
								importFileIdentifier: value,
							})
							markUnsaved()
						}}
					/>
				</Col>
			</Row>
		</ContentWrapper>
	)

	// Settings of the "merge bike routes" functionality: a gear next to its
	// switch (only while it's on) opening a popover, so the card grid stays even
	const renderBikeMergeMinTimeSaved = () => {
		const percent = globalSettings?.bikeMergeMinTimeSavedPercent ?? 20
		// Two 30 min warehouse trips (60 min in total), as a worked example
		const maxMergedMinutes = Math.round(60 * (1 - percent / 100))
		const example =
			percent >= 100
				? Strings.contracts.bikeMergeMinTimeSavedNever
				: Strings.contracts.bikeMergeMinTimeSavedExample
						.replace("{percent}", String(percent))
						.replace("{minutes}", String(maxMergedMinutes))

		const content = (
			<div style={{ width: 280 }}>
				<div style={{ fontSize: 12, color: "#8c8c8c" }}>
					{Strings.contracts.bikeMergeMinTimeSavedHelper}
				</div>
				<Input
					id="contract_bike_merge_min_time_saved"
					aria-label={Strings.contracts.bikeMergeMinTimeSaved}
					type="number"
					min="0"
					max="100"
					step="1"
					suffix="%"
					style={{ width: 110, marginTop: 10 }}
					value={percent}
					onChange={(e: any) => {
						const value = Math.min(
							100,
							Math.max(0, parseInt(e.target.value, 10) || 0)
						)
						setGlobalSettings({
							...globalSettings,
							bikeMergeMinTimeSavedPercent: value,
						})
						markUnsaved()
					}}
				/>
				<div
					style={{
						marginTop: 10,
						padding: "8px 10px",
						backgroundColor: "#fafafa",
						borderRadius: 6,
						fontSize: 12,
						color: "#595959",
					}}
				>
					<div>{example}</div>
					<div style={{ marginTop: 4 }}>
						{Strings.contracts.bikeMergeMinTimeSavedRecommended}
					</div>
				</div>
			</div>
		)

		return (
			<Popover
				trigger="click"
				placement="bottomRight"
				title={Strings.contracts.bikeMergeMinTimeSaved}
				content={content}
			>
				<Tooltip title={Strings.contracts.bikeMergeMinTimeSaved}>
					<SettingOutlined
						aria-label={Strings.contracts.bikeMergeMinTimeSaved}
						style={{
							marginRight: 12,
							fontSize: 16,
							color: "#8c8c8c",
							cursor: "pointer",
						}}
					/>
				</Tooltip>
			</Popover>
		)
	}

	const renderAttachments = () => (
		<Table
			title={{
				icon: "grampo",
				title: Strings.contracts.attachments,
			}}
			style={{ marginBottom: 10 }}
			data={attachments}
			columns={[
				{
					Header: Strings.fields.description,
					id: "description",
					accessor: (row: any) => translate(row.description) || "-",
				},
			]}
			add={{
				onClick: () => setShowAttachmentsDrawer(true),
			}}
			actions={{
				view: (original: any) => ({
					onClick: () => window.open(original.file, "_blank"),
				}),
				edit: (original: any) => ({
					onClick: () => {
						setShowAttachmentsDrawer(true)
						setTempAttachment(JSON.parse(JSON.stringify(original)))
					},
				}),
				remove: (original: any) => ({
					onClick: () => deleteAttachment(original._id),
				}),
			}}
		/>
	)

	const renderSidebarContent = () => {
		let element: any
		if (tempAttachment?.file?.type?.includes("image")) {
			element = <Icon name="frame" />
		} else if (tempAttachment?.file?.type?.includes("pdf")) {
			element = <img src={pdfImage} alt="excel" />
		} else if (
			tempAttachment?.file?.type?.includes("xls") ||
			tempAttachment?.file?.type?.includes("xlsx") ||
			tempAttachment?.file?.type?.includes("spreadsheet")
		) {
			element = <img src={excelImage} alt="excel" />
		} else {
			element = <Icon name="arquivo" />
		}

		return (
			<Row gutter={[0, 10]}>
				<Col xs={24}>
					<div className="DrawerLanguagePicker">
						<Select
							optionFilterProp="children"
							onChange={(value: any) => setLanguage(value)}
							filterOption={(input: any, option: any) =>
								option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							value={language}
							showArrow
							dropdownMatchSelectWidth
						>
							{LANGUAGES.map((lang: any, index: number) => (
								<Select.Option
									key={`select_option_${lang}_${index}`}
									value={lang.value}
								>
									{lang.label}
								</Select.Option>
							))}
						</Select>
					</div>
				</Col>
				<Col xs={24}>
					<label
						htmlFor="attachment_upload"
						className="InputLabel --label-required"
					>
						{Strings.contracts.attachment}
					</label>
					<div className="AttachmentFile">
						<Dropzone
							className="GenericAttachmentUpload"
							id="attachment_upload"
							multiple={false}
							onDrop={(files: any) => {
								const newFilesToDelete = [] as any

								if (typeof tempAttachment?.file === "string") {
									newFilesToDelete.push(tempAttachment.file)
								}

								setTempAttachment({
									...tempAttachment,
									file: files[0],
								})
								setFilesToDelete(newFilesToDelete)
							}}
						>
							{tempAttachment?.file ? (
								<div className="AttachmentFileType --visible">
									{element}
									<p>
										{tempAttachment?.file?.name || Strings.contracts.document}
									</p>
								</div>
							) : (
								<div
									className={`GenericAttachmentUploadOverlay${
										!tempAttachment?.file ? " --visible" : ""
									}`}
								>
									<Icon name="grampo" />
									<span>{Strings.generic.changeFile}</span>
								</div>
							)}
							{tempAttachment?.file && (
								<button
									onClick={(e: React.MouseEvent<HTMLElement>) => {
										e.stopPropagation()
										const newFilesToDelete = [] as any

										if (typeof tempAttachment?.file === "string") {
											newFilesToDelete.push(tempAttachment.file)
										}

										setTempAttachment({
											...tempAttachment,
											file: null,
										})
										setFilesToDelete(newFilesToDelete)
									}}
									className="GenericAttachmentDelete"
								>
									<Icon name="close" />
								</button>
							)}
						</Dropzone>
					</div>
				</Col>
				<Col xs={24}>
					<label
						htmlFor="attachment_description"
						className="InputLabel --label-required"
					>
						{Strings.fields.description}
					</label>
					<Input.TextArea
						id="attachment_description"
						style={{ resize: "none" }}
						placeholder={Strings.fields.description}
						value={tempAttachment?.description?.[language] || ""}
						onChange={(e: any) => {
							setTempAttachment({
								...tempAttachment,
								description: {
									...tempAttachment?.description,
									[language]: e.target.value,
								},
							})
						}}
						rows={4}
					/>
				</Col>
			</Row>
		)
	}

	const renderDrawer = () => (
		<Drawer
			title={
				<div className="SidebarTitleContainer">
					<Icon name="grampo" />
					<p>
						{tempAttachment?._id
							? Strings.contracts.editAttachment
							: Strings.contracts.addAttachment}
					</p>
				</div>
			}
			footer={
				<div className="SidebarFooterContainer">
					<button
						type="button"
						className="SidebarFooterButton --button-confirm"
						onClick={() => submitAttachment()}
					>
						{Strings.generic.confirm}
					</button>
					<button
						type="button"
						className="SidebarFooterButton --button-cancel"
						onClick={() => {
							setShowAttachmentsDrawer(false)
							setTempAttachment(null)
						}}
					>
						{Strings.generic.cancel}
					</button>
				</div>
			}
			placement="right"
			width={mobile ? "100%" : 400}
			onClose={() => {
				setTempAttachment(null)
				setShowAttachmentsDrawer(false)
			}}
			visible={showAttachmentsDrawer}
		>
			{renderSidebarContent()}
		</Drawer>
	)

	return (
		<div className="ScreenContractDetail">
			<Helmet>
				<title>{Strings.businesses.contracts}</title>
				<meta
					name="description"
					content="Create or edit contracts for a specific client"
				/>
			</Helmet>
			{renderGeneralInformation()}
			{renderLocation()}
			{renderDrivers()}
			{renderVehicles()}
			{renderOperators()}
			{renderShifts()}
			{renderWorkAreas()}
			{contractId !== "new" && renderContractZones()}
			{renderLots()}
			{renderFunctionalities()}
			{renderSmsOptions()}
			{renderSmsTestModeOptions()}
			{renderScannerEmail()}
			{renderServiceTime()}
			{renderOptimizerParams()}
			{renderPreProcessingParams()}
			{renderContainerSizes()}
			{renderExternalDelivery()}
			{renderGlobalSettings()}
			{contractId !== "new" && renderAvailableGeojsons()}
			{contractId !== "new" && renderAttachments()}
			{renderDrawer()}
		</div>
	)
}

export default ContractDetail
